/* HR/ERP Admin Shell — V2 editorial design language
 *
 * Provides:
 *   - Design tokens (HR.*) lifted from variant-a-v2.jsx
 *   - api()   : fetch wrapper with JWT + JSON + error normalization
 *   - useEnum(): React hook backed by /api/enum cache
 *   - <Shell> : sidebar + top bar + outlet, hash-based router
 *   - <Modal>, <Field>, <Button>, <Pill>, <DataTable>, <Toast> primitives
 *
 * Loaded before hr-departments.jsx / hr-employees.jsx.
 */

const HR = {
  bg:         '#F6F4EF',
  surface:    '#FFFFFF',
  surfaceAlt: '#FAF8F3',
  ink:        '#0E1411',
  inkSoft:    '#4A5048',
  inkFaint:   '#8B8F89',
  rule:       '#E5E2DA',
  ruleSoft:   '#EEEBE3',
  accent:     '#0B5F5A',
  accentSoft: '#E6EEED',
  up:         '#0B5F5A',
  down:       '#9B3330',
  warn:       '#A56A1F',
  sans:       '"Söhne","Inter","Helvetica Neue",system-ui,sans-serif',
  serif:      '"Source Serif 4","GT Sectra","Fraunces",Georgia,serif',
  mono:       '"JetBrains Mono","SF Mono",Menlo,monospace',
  // Type scale (8 levels) — single source of truth for fontSize across modules.
  // Use HR.text.{key} instead of raw numeric fontSize.
  text: {
    xs:      10.5, // tiny captions, badges
    sm:      12,   // table cells, secondary
    base:    13,   // body
    md:      14,   // form labels, sidebar items
    lg:      17,   // sub-section headers
    xl:      22,   // card titles
    '2xl':   32,   // major stats
    display: 44,   // page hero / huge numbers
  },
  // Modal width tokens (3 tiers) — see <Modal size="sm|md|lg" />
  modal: { sm: 440, md: 640, lg: 860 },
  // Spacing scale (multiples of 4)
  space: { xs: 4, sm: 8, md: 12, lg: 18, xl: 24, '2xl': 32, '3xl': 48 },
};
if (window.GxCore) window.GxCore.adoptTokens(HR, 'HR');  // Expressive only (no-op under Bento): fonts + colour keys → var(--gx-*) (SPEC §1.2 內頁換膚)

/* ─────────────────────────  API client  ───────────────────────── */

const API_BASE = '/api';

function getToken() {
  return localStorage.getItem('token') || '';
}

function setToken(t) {
  if (t) localStorage.setItem('token', t);
  else localStorage.removeItem('token');
}

/**
 * Universal fetch wrapper.
 *  - Adds Authorization: Bearer <jwt> if available.
 *  - Body objects are JSON-encoded with Content-Type set.
 *  - Resolves to parsed JSON; throws { status, detail } on non-2xx.
 *  - 401 → clears token (caller may redirect).
 */
async function api(method, path, body) {
  const headers = { 'Accept': 'application/json' };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const init = { method, headers };
  if (body !== undefined && body !== null) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  const res = await fetch(API_BASE + path, init);
  let data = null;
  const text = await res.text();
  if (text) {
    try { data = JSON.parse(text); } catch { data = { raw: text }; }
  }
  if (!res.ok) {
    if (res.status === 401) setToken('');
    // 把 detail normalize 成 string — FastAPI HTTPException 回 string，
    // 但 Pydantic 422 會回 array of {type, loc, msg, input}，直接塞給 React render 會崩潰。
    let detail = data && data.detail;
    if (Array.isArray(detail)) {
      detail = detail
        .map(e => {
          const where = Array.isArray(e?.loc) ? e.loc.filter(x => x !== 'body').join('.') : '';
          return where ? `${where}: ${e.msg || ''}` : (e.msg || '');
        })
        .filter(Boolean)
        .join('; ');
    }
    if (typeof detail !== 'string') detail = JSON.stringify(detail) || `HTTP ${res.status}`;
    if (!detail) detail = `HTTP ${res.status}`;
    throw { status: res.status, detail, data };
  }
  return data;
}

/* ─────────────────────────  Enum cache  ───────────────────────── */

const EnumContext = React.createContext({ types: {}, loading: true });

function EnumProvider({ children }) {
  const [state, setState] = React.useState({ types: {}, loading: true });
  React.useEffect(() => {
    // 受限帳號（analyst / analyst_lite / non_staff，無 privileged role）：
    // /api/enum 不在非員工閘門白名單內（一律 403，SPEC §1.2）——直接跳過
    // fetch，維持原本的空 enum 降級行為，不再空打一發 403。
    if (isRestrictedAccount(window._USER)) {
      setState({ types: {}, loading: false });
      return;
    }
    let alive = true;
    api('GET', '/enum')
      .then(res => { if (alive) setState({ types: res.types || {}, loading: false }); })
      .catch(() => { if (alive) setState({ types: {}, loading: false }); });
    return () => { alive = false; };
  }, []);
  return <EnumContext.Provider value={state}>{children}</EnumContext.Provider>;
}

/* Cross-tab logout sync: if main page (or any other tab) clears the token,
 * reload this tab so the AuthGate falls back to LoginScreen. */
if (typeof window !== 'undefined' && !window.__hrTokenSyncWired) {
  window.__hrTokenSyncWired = true;
  window.addEventListener('storage', (e) => {
    if (e.key === 'token' && !e.newValue) window.location.reload();
  });
}

/* ─────────────────────────  Server-Sent Events (SSE)  ─────────────────────────
 * 全站單一 EventSource — admin 對 user 寫操作後，所有 client 即時收到。
 * 用法：const last = useServerEvent('users.changed'); React.useEffect(() => refetch(), [last]);
 * ──────────────────────────────────────────────────────────────────────────── */

const _sseListeners = {};  // { eventName: Set<callback> }
let _sseStarted = false;

function _startSSE() {
  if (_sseStarted || typeof window === 'undefined') return;
  const token = getToken();
  if (!token) return;
  _sseStarted = true;

  // EventSource 不支援自訂 header，token 用 query string 傳會洩漏進 access log。
  // 折衷：透過 cookie 傳遞 — 但本專案目前用 localStorage 不是 cookie；先用 query string，
  //   後端 access log 路徑遮罩 token。後續若改 cookie auth 可去掉這步驟。
  const url = API_BASE + '/events?token=' + encodeURIComponent(token);
  try {
    const es = new EventSource(url, { withCredentials: false });
    es.onerror = () => { /* 瀏覽器會自動重連，不用人為干預 */ };
    // 監聽所有事件名稱
    const dispatch = (ev) => {
      const set = _sseListeners[ev.type];
      if (!set) return;
      let data = null;
      try { data = JSON.parse(ev.data); } catch { /* keep null */ }
      set.forEach(cb => { try { cb(data); } catch {} });
    };
    ['users.changed', 'roles.changed', 'notifications.changed', 'ping'].forEach(name => es.addEventListener(name, dispatch));
    window.__sseConnection = es;
  } catch (err) {
    console.warn('[SSE] init failed:', err);
    _sseStarted = false;
  }
}

/** Subscribe to a server-sent event. Returns the latest payload (or null).
 *  Re-render happens whenever the event fires; downstream effects can depend on `last`.
 */
function useServerEvent(eventName) {
  const [last, setLast] = React.useState(null);
  React.useEffect(() => {
    _startSSE();
    if (!_sseListeners[eventName]) _sseListeners[eventName] = new Set();
    const cb = (data) => setLast({ data, at: Date.now() });
    _sseListeners[eventName].add(cb);
    return () => _sseListeners[eventName].delete(cb);
  }, [eventName]);
  return last;
}

/** Returns { options, byCode, label(code) } for a given enum_type. */
function useEnum(type) {
  const { types } = React.useContext(EnumContext);
  const options = types[type] || [];
  const byCode = React.useMemo(() => {
    const m = {};
    options.forEach(o => { m[o.code] = o; });
    return m;
  }, [options]);
  return {
    options,
    byCode,
    label: (code) => (byCode[code] && byCode[code].name) || code || '—',
    color: (code) => (byCode[code] && byCode[code].color) || null,
  };
}

/* ─────────────────────────  Hash router  ───────────────────────── */

function useHashRoute() {
  const [hash, setHash] = React.useState(window.location.hash || '#/dashboard');
  React.useEffect(() => {
    const onChange = () => setHash(window.location.hash || '#/dashboard');
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  // hash form: '#/employees' or '#/employees/42' or '#/employees?q=foo'
  // Strip query string before splitting into route segments
  const qIdx = hash.indexOf('?');
  const path = qIdx < 0 ? hash : hash.slice(0, qIdx);
  const [, route, ...rest] = path.split('/');
  return { route: route || 'dashboard', params: rest, raw: hash };
}

function navigate(path) {
  window.location.hash = path.startsWith('#') ? path : '#' + path;
}

/* Hash-query helpers — let pages persist filter state in the URL.
 *
 * Hash format: #/employees?dept=2&status=active&q=alice
 *   getHashQuery() → { dept: '2', status: 'active', q: 'alice' }
 *   setHashQuery({ dept: 5, q: '' })  // empty/null/undefined values are stripped
 *
 * setHashQuery uses history.replaceState so it does NOT trigger a hashchange
 * event — that means components that subscribe to the route via useHashRoute
 * won't re-render in a loop when filters change.
 */
function getHashQuery() {
  const h = window.location.hash || '';
  const qIdx = h.indexOf('?');
  if (qIdx < 0) return {};
  const params = new URLSearchParams(h.slice(qIdx + 1));
  const out = {};
  params.forEach((v, k) => { out[k] = v; });
  return out;
}

function setHashQuery(obj) {
  const h = window.location.hash || '#/';
  const qIdx = h.indexOf('?');
  const base = qIdx < 0 ? h : h.slice(0, qIdx);
  const params = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '' || v === false) return;
    params.set(k, String(v));
  });
  const qs = params.toString();
  const next = qs ? `${base}?${qs}` : base;
  // Use replaceState so we don't fire hashchange (avoids re-render loops)
  // and don't pollute browser history with every keystroke.
  history.replaceState(null, '', window.location.pathname + window.location.search + next);
}

/* ─────────────────────────  Toast (top-right) ─────────────────── */

const ToastContext = React.createContext(() => {});

function ToastProvider({ children }) {
  const [items, setItems] = React.useState([]);
  const push = React.useCallback((msg, kind = 'info') => {
    const id = Math.random().toString(36).slice(2);
    setItems(arr => [...arr, { id, msg, kind }]);
    setTimeout(() => setItems(arr => arr.filter(x => x.id !== id)), 3500);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div style={{
        position: 'fixed', top: 20, right: 20, zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        {items.map(t => (
          <div key={t.id} style={uiStyle({
            padding: '10px 14px', borderRadius: 5, fontSize: FontSizes.xs,
            background: t.kind === 'error' ? HR.down : t.kind === 'ok' ? HR.accent : HR.ink,
            color: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxWidth: 360, fontFamily: HR.sans,
          }, {
            // Expressive: mockup `.snack` (inverse surface for info)
            padding: '14px 20px', borderRadius: 10, fontSize: 14, fontWeight: 700, lineHeight: 1.4,
            background: t.kind === 'error' ? GxT.color.down : t.kind === 'ok' ? GxT.color.p : GxT.color.inv,
            boxShadow: GxT.shadow.pop, fontFamily: GxT.font,
          })}>{t.msg}</div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const useToast = () => React.useContext(ToastContext);

/* ─────────────────────────  Primitives  ───────────────────────── */

function Button({ variant = 'secondary', size = 'md', children, icon, ...rest }) {
  const base = {
    border: 'none', cursor: 'pointer', fontFamily: HR.sans,
    display: 'inline-flex', alignItems: 'center', gap: 7,
    letterSpacing: '0.01em', fontWeight: 500, borderRadius: 4,
    transition: 'background 120ms, border-color 120ms, box-shadow 120ms',
  };
  const sizes = {
    sm: { padding: '5px 10px', fontSize: FontSizes.xxs },
    md: { padding: '8px 14px', fontSize: FontSizes.xs },
    lg: { padding: '11px 18px', fontSize: FontSizes.xs },
  };
  // Ghost: subtle 1px border so it reads as a button (not text) when always-visible
  const variants = {
    primary:   { background: HR.accent, color: '#fff', boxShadow: '0 1px 0 rgba(11,95,90,0.3)' },
    secondary: { background: HR.surface, color: HR.ink, border: `1px solid ${HR.rule}` },
    ghost:     { background: HR.surface, color: HR.inkSoft, border: `1px solid ${HR.ruleSoft}` },
    danger:    { background: HR.surface, color: HR.down, border: `1px solid ${HR.rule}` },
  };
  // Expressive (plan-expressive-inner-pages §2(b)): GX layers go AFTER the Bento variant (which carries its
  // own border) and BEFORE the caller's style, so e.g. ConfirmDialog's red background still wins.
  let style;
  if (gxUiOn()) {
    const C = GxT.color;
    const gxBase = {
      fontFamily: GxT.font, fontWeight: 700, letterSpacing: 0, border: 'none', lineHeight: 1.2,
      transition: 'border-radius .4s var(--gx-spring), transform .4s var(--gx-spring), background .2s',
    };
    const gxSizes = {
      sm: { minHeight: 32, padding: '0 14px', fontSize: 13, borderRadius: GxT.radius.btnSm },
      md: { minHeight: 40, padding: '0 18px', fontSize: 14, borderRadius: GxT.radius.btn },
      lg: { minHeight: 48, padding: '0 24px', fontSize: 15, borderRadius: GxT.radius.btnLg },
    };
    const gxVariants = {
      primary:   { background: C.p, color: C.onP, border: 'none', boxShadow: 'none' },            // filled
      secondary: { background: C.sc, color: C.onSc, border: 'none', boxShadow: 'none' },          // tonal
      ghost:     { background: 'transparent', color: C.onV, border: `1px solid ${C.olv}`, boxShadow: 'none' },  // outlined
      danger:    { background: GxT.tint.down.bg, color: C.down, border: 'none', boxShadow: 'none' },  // tonal danger
    };
    style = { ...base, ...sizes[size], ...variants[variant], ...gxBase, ...gxSizes[size], ...gxVariants[variant], ...(rest.style || {}) };
  } else {
    style = { ...base, ...sizes[size], ...variants[variant], ...(rest.style || {}) };
  }
  return (
    <button
      data-hr-btn={variant}
      {...rest}
      style={style}
    >
      {icon && <Icon name={icon} size={size === 'sm' ? uiPick(11, 14) : uiPick(13, 16)} stroke={2} />}
      {children}
    </button>
  );
}

/* Global hover affordances — injected once via a fixed style tag. */
if (typeof document !== 'undefined' && !document.getElementById('hr-btn-global-style')) {
  const s = document.createElement('style');
  s.id = 'hr-btn-global-style';
  s.textContent = `
    button[data-hr-btn="ghost"]:hover:not(:disabled) {
      background: ${HR.surfaceAlt}; border-color: ${HR.rule}; color: ${HR.ink};
    }
    button[data-hr-btn="secondary"]:hover:not(:disabled) {
      background: ${HR.surfaceAlt}; border-color: ${GxCore.alpha(HR.accent, '55')};
    }
    button[data-hr-btn="primary"]:hover:not(:disabled) {
      box-shadow: 0 2px 6px rgba(11,95,90,0.4), 0 1px 0 rgba(11,95,90,0.3);
      transform: translateY(-0.5px);
    }
    button[data-hr-btn="danger"]:hover:not(:disabled) {
      background: #F4DAD8; border-color: ${HR.down};
    }
  `;
  document.head.appendChild(s);
}

function Pill({ tone = 'neutral', children, dot = false }) {
  const tones = {
    neutral: { bg: HR.ruleSoft, fg: HR.inkSoft },
    accent:  { bg: HR.accentSoft, fg: HR.accent },
    warn:    { bg: '#F4E9D6', fg: HR.warn },
    down:    { bg: '#F4DAD8', fg: HR.down },
  };
  // Expressive reads the shared tint table (also knows success/ok, info, done); Bento tones unchanged.
  const t = gxUiOn() ? GxCore.tone(tone) : (tones[tone] || tones.neutral);
  return (
    <span style={uiStyle({
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 8px', borderRadius: 10, fontSize: FontSizes.trading_lg,
      background: t.bg, color: t.fg, fontWeight: 500,
      fontFamily: HR.sans,
    }, {
      padding: '3px 10px', borderRadius: GxT.radius.chip, fontSize: 12, fontWeight: 700,
      fontFamily: GxT.font, whiteSpace: 'nowrap',
    })}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: t.fg }} />}
      {children}
    </span>
  );
}

function Field({ label, hint, error, children, required, span }) {
  return (
    <div style={{ gridColumn: span ? `span ${span}` : 'auto', display: 'flex', flexDirection: 'column', gap: uiPick(5, 6) }}>
      <label style={uiStyle({
        fontSize: FontSizes.trading_lg, letterSpacing: '0.08em', textTransform: 'uppercase',
        color: HR.inkFaint, fontWeight: 500,
      }, {
        fontSize: 13, letterSpacing: 0, textTransform: 'none', color: GxT.color.onV, fontWeight: 700,
      })}>
        {label}{required && <span style={{ color: HR.down, marginLeft: 3 }}>*</span>}
      </label>
      {children}
      {error && <span style={{ fontSize: uiPick(FontSizes.trading_lg, 12), color: HR.down }}>{error}</span>}
      {!error && hint && <span style={{ fontSize: uiPick(FontSizes.trading_lg, 12), color: HR.inkFaint }}>{hint}</span>}
    </div>
  );
}

function inputStyle(opts = {}) {
  const bento = {
    padding: '8px 11px', fontSize: FontSizes.xs, fontFamily: HR.sans,
    border: `1px solid ${HR.rule}`, borderRadius: 4,
    background: HR.surface, color: HR.ink, outline: 'none',
    width: '100%', ...opts,
  };
  if (!gxUiOn()) return bento;
  // Expressive: outlined field (white card + 1px ol border, matching the outlined pills/buttons).
  // The ol (#848881) border keeps the field edge ≥3:1 on white (plan review #3) — do not use olv.
  // Hover / focus ring / disabled colours come from gx-css-pages. Caller opts still win (width, mono…).
  return {
    ...bento,
    padding: '10px 12px', minHeight: 40, fontSize: 14, fontFamily: GxT.font,
    border: `1px solid ${GxT.color.ol}`, borderRadius: GxT.radius.input,
    background: GxT.color.card, color: GxT.color.on,
    ...opts,
  };
}

function TextInput({ value, onChange, placeholder, type = 'text', mono = false, ...rest }) {
  return (
    <input
      type={type}
      value={value ?? ''}
      placeholder={placeholder}
      onChange={(e) => onChange && onChange(e.target.value)}
      style={inputStyle({ fontFamily: mono ? HR.mono : HR.sans })}
      {...rest}
    />
  );
}

function Select({ value, onChange, options, placeholder = '— 請選擇 —', allowEmpty = true }) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange && onChange(e.target.value || null)}
      style={inputStyle({ appearance: 'none', backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${uiPick('%238B8F89', '%234A5048')}' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>")`, backgroundRepeat: 'no-repeat', backgroundPosition: uiPick('right 10px center', 'right 12px center'), paddingRight: uiPick(28, 32) })}
    >
      {allowEmpty && <option value="">{placeholder}</option>}
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function Textarea({ value, onChange, rows = 3, placeholder }) {
  return (
    <textarea
      value={value ?? ''}
      placeholder={placeholder}
      rows={rows}
      onChange={(e) => onChange && onChange(e.target.value)}
      style={inputStyle({ fontFamily: HR.sans, resize: 'vertical' })}
    />
  );
}

function Checkbox({ checked, onChange, label }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: uiPick(FontSizes.xs, 14), cursor: 'pointer', color: HR.ink }}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange && onChange(e.target.checked)}
        style={{ width: uiPick(15, 18), height: uiPick(15, 18), accentColor: uiPick(HR.accent, GxT.color.p) }}
      />
      {label}
    </label>
  );
}

function Modal({ open, onClose, title, subtitle, children, footer, size = 'md', width }) {
  if (!open) return null;
  // size: 'sm' (confirm/short form, 440) · 'md' (typical form, 640) · 'lg' (wide form / multi-section, 860)
  // `width` (legacy numeric) still honoured for backwards compat — prefer `size`.
  const resolvedWidth = width != null ? width : (HR.modal[size] || HR.modal.md);
  // Expressive: values only (uiStyle) — the panel's direct child <div>s must keep their number and
  // order because index.html ≤767 pads them by position (plan review #12).
  return (
    <div
      className="hr-modal-overlay"
      onClick={onClose}
      style={uiStyle({
        position: 'fixed', inset: 0, background: 'rgba(14,20,17,0.45)', zIndex: 1000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '60px 20px', overflowY: 'auto',
      }, { background: GxT.color.scrim, padding: '48px 16px' })}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={uiStyle({
          width: resolvedWidth, maxWidth: '100%', background: HR.surface, borderRadius: 6,
          boxShadow: '0 24px 64px rgba(0,0,0,0.25)', overflow: 'hidden',
          display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 120px)',
        }, {
          background: GxT.color.card, borderRadius: GxT.radius.modal, boxShadow: GxT.shadow.modal,
          maxHeight: 'calc(100vh - 96px)',
        })}
      >
        <div style={uiStyle({ padding: '20px 24px', borderBottom: `1px solid ${HR.rule}` }, { padding: '22px 24px 8px', borderBottom: 'none' })}>
          <div style={uiStyle({ fontSize: FontSizes.trading_lg, letterSpacing: '0.14em', textTransform: 'uppercase', color: HR.inkFaint, marginBottom: 4 }, {
            fontSize: 13, fontWeight: 700, color: GxT.color.p, letterSpacing: 0, textTransform: 'none',
          })}>
            {subtitle || 'Action'}
          </div>
          <h2 style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.lg, fontWeight: 400, margin: 0, letterSpacing: '-0.015em' }, {
            fontFamily: GxT.font, fontSize: GxT.size.h2, fontWeight: GxT.weight.title, letterSpacing: '-0.01em', lineHeight: 1.25, color: GxT.color.on,
          })}>
            {title}
          </h2>
        </div>
        <div style={uiStyle({ padding: '20px 24px', overflowY: 'auto', flex: 1 }, { padding: '12px 24px 20px' })}>
          {children}
        </div>
        {footer && (
          <div style={uiStyle({
            padding: '14px 24px', borderTop: `1px solid ${HR.rule}`, background: HR.surfaceAlt,
            display: 'flex', justifyContent: 'flex-end', gap: 8,
          }, { padding: '8px 24px 20px', borderTop: 'none', background: 'transparent', flexWrap: 'wrap' })}>{footer}</div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────  Login  ───────────────────────── */

/* Centralised login flow — no longer renders here.
 * AuthGate redirects to /static/login.html (V2 design) which on success
 * lands user on /static/index.html (main trading site).
 * Kept as a compat alias so any existing reference still resolves. */
function LoginScreen() {
  React.useEffect(() => {
    const next = encodeURIComponent(window.location.pathname + window.location.hash);
    window.location.href = `/static/login.html?redirect=${next}`;
  }, []);
  return (
    <div style={{
      minHeight: '100vh', background: HR.bg, color: HR.ink,
      fontFamily: HR.sans, display: 'grid', placeItems: 'center',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: HR.inkSoft }}>
        <Spinner size={18} /> 正在跳轉到登入頁…
      </div>
    </div>
  );
}

/* Forbidden state — shown when user navigates (deep link) to a route their
 * role can't access. Sidebar should already have hidden it, but URL probe
 * still needs server-grade gating in UI. */
function Forbidden({ route, user }) {
  const r = ROUTES.find(x => x.key === route);
  const requiredLabel = !r ? '未知'
                      : (r.roles === undefined) ? '任何登入者'
                      : r.roles.length === 0 ? 'admin'
                      : r.roles.concat('admin').join(' / ');
  return (
    <div style={{
      padding: '80px 40px', maxWidth: 640, margin: '0 auto', textAlign: 'center',
    }}>
      <div style={uiStyle({
        display: 'inline-flex', padding: 18, borderRadius: '50%',
        background: '#F4DAD8', color: HR.down, marginBottom: 22,
      }, { borderRadius: GxT.radius.card, background: GxT.tint.down.bg })}>
        <Icon name="log-out" size={28} stroke={2} />
      </div>
      <h1 style={uiStyle({
        fontFamily: HR.serif, fontSize: FontSizes.xl, fontWeight: 400,
        letterSpacing: '-0.02em', marginBottom: 12,
      }, { fontFamily: GxT.font, fontSize: GxT.size.h1, fontWeight: GxT.weight.display, letterSpacing: '-0.5px', lineHeight: 1.12 })}>
        權限不足
      </h1>
      <p style={{ fontSize: FontSizes.sm, color: HR.inkSoft, lineHeight: 1.7, marginBottom: 24 }}>
        你目前的角色（{(userRoles(user) || []).join(', ') || '無'}）無法存取「
        <strong style={{ color: HR.ink }}>{r?.label || route}</strong>」。
        <br/>此功能需要 <code style={{ background: HR.ruleSoft, padding: '1px 6px', borderRadius: uiPick(3, GxT.radius.chipSm), fontFamily: HR.mono }}>{requiredLabel}</code> 權限。
      </p>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
        <Button variant="secondary" icon="arrow-right" onClick={() => navigate('/dashboard')}>
          回到總覽
        </Button>
        <Button variant="ghost" onClick={() => { window.location.href = '/static/index.html'; }}>
          回主站
        </Button>
      </div>
    </div>
  );
}

/* ─────────────────────────  Shell (sidebar + top bar)  ─────────── */

/* Role-based route config.
 *
 * `roles` rule: any role in the list grants access. Empty/omitted = everyone (logged in).
 * `admin` always passes (treated as super-user — bypass check below in canAccess()).
 *
 * Roles per api-spec.md §0.2:
 *   admin · hr · finance · manager · trade_editor · employee
 */
/* Routes — ERP-strict sidebar grouped by USER INTENT.
 *
 *  Sidebar 只顯示 ERP 相關項目。其他工具（trading / lunch / courses / research / files）
 *  仍是合法路由（程式碼還在、Cmd+K 可搜尋、URL 可直接訪問），但不出現在 ERP sidebar，
 *  避免認知模式混淆 — ERP 是「管理工作」、Trading 是「專注操作」。
 *
 *  Sections:
 *    〈總覽〉            Dashboard
 *    〈日常〉            出勤 / 請假 / 報帳 / 行事曆          (every employee)
 *    〈人員管理〉        員工 / 部門 / 報表                    (hr / finance)
 *    〈財務〉            薪資 / 發票・總帳                     (finance)
 *    〈管理〉            設定 / 角色 / 審計 / Token            (admin only)
 */
/* ERP route catalog — used by:
 *   • TradingSidebar (variant-a-v2.jsx) to render the collapsible 「企業服務」 group
 *   • CommandPalette (hr-cmdk.jsx) for the "Pages" source
 *   • canAccessRoute() to gate direct-URL access by role
 * The previous ERP 'dashboard' overview was removed in the unified-shell
 * refactor — index.html's #/ now serves as the workspace landing page. */
const ROUTES = [
  // 〈日常〉— daily ops (every employee)
  { key: 'attendance',   label: '出勤',         icon: 'clock',        section: '日常' },
  { key: 'leave',        label: '假勤申請',     icon: 'calendar',     section: '日常' },
  { key: 'expense',      label: '報帳',         icon: 'dollar-sign',  section: '日常' },
  { key: 'calendar',     label: '行事曆',       icon: 'calendar',     section: '日常' },

  // 〈人員管理〉— HR
  { key: 'employees',    label: '員工',         icon: 'file-text',    section: '人員管理', roles: [] },
  { key: 'departments',  label: '部門',         icon: 'folder',       section: '人員管理', roles: [] },
  { key: 'reports',      label: '報表中心',     icon: 'trending-up',  section: '人員管理', roles: ['finance'] },

  // 〈財務〉— finance
  { key: 'payroll',      label: '薪資',         icon: 'dollar-sign',  section: '財務' /* employee sees own; finance/hr sees all */ },
  { key: 'invoices',     label: '發票・總帳',   icon: 'file-text',    section: '財務', roles: ['finance', 'hr'] },

  // 〈管理〉— admin only
  // 系統設定前端先註解保留（後端 routes_settings 仍可用）— 待 UI 重新設計後再啟用
  // { key: 'settings',     label: '系統設定',     icon: 'settings',     section: '管理', roles: [] },
  { key: 'roles',        label: '角色 & 權限',  icon: 'settings',     section: '管理', roles: [] },
  { key: 'audit',        label: '審計日誌',     icon: 'search',       section: '管理', roles: [] },
  { key: 'admin-tools',  label: 'Token 使用',   icon: 'activity',     section: '管理', roles: [] },
];

/* Routes that exist in the trading sidebar but not in ROUTES (which is ERP-only).
 * Registered here so Cmd+K can find them and canAccessRoute() can gate them.
 * Single-shell now — no `external` field needed (all routes live in index.html). */
const EXTRA_ROUTES = [
  { key: 'updates', label: '更新消息', icon: 'bell', section: '工作台' },
  { key: 'dashboard', label: '交易儀表板',  icon: 'trending-up', section: '交易' },
  { key: 'lunch',     label: '午餐訂購',    icon: 'coffee',      section: '工具' },
  { key: 'courses',   label: '教學',        icon: 'book',        section: '工具' },
  { key: 'research',  label: 'Deep Research', icon: 'search',    section: '工具' },
  { key: 'files',     label: '文件知識庫',  icon: 'folder',      section: '工具' },
  // 分析工作台 — 全帳號共用工作台（hr-analyst-hub.jsx；analyst 兩級的落地頁）；
  // 問候列 + 今日情報 + 個股快查 + 工具卡片牆（/api/tools manifest）。
  // section 自 2026-08-24 起改用側欄三分組名（每日情報/個股與量化/深度研究，
  // 對照 variant-a-v2.jsx 統一側欄）——section 是 Cmd+K 的可見副標與可搜欄位。
  { key: 'hub', label: '分析工作台', icon: 'layout', section: '工作台' },
  { key: 'daily-brief', label: '市場新聞',  icon: 'bell',        section: '每日情報' },
  { key: 'analysis-report', label: '研究報告庫', icon: 'trending-up', section: '每日情報' },
  { key: 'morning-report', label: '早晨報告', icon: 'clock', section: '每日情報' },
  { key: 'morning-focus', label: '盤前重點', icon: 'clock', section: '每日情報' },
  // 分點調查局（hr-fendian.jsx）：代理內網 chipk-fendian 收集端每日分點彙整
  // （/api/fendian）。**員工限定**：無 roles 欄位 → 一般員工/admin 可見；刻意不進
  // _ANALYST_ROUTES / _ANALYST_LITE_ROUTES / _NON_STAFF_ROUTES，受限帳號在
  // canAccessRoute 先被 restrictedRouteKeys 白名單擋下（鏡射後端：/api/fendian
  // 不在任何非員工白名單）。
  { key: 'fendian', label: '分點調查局', icon: 'bar-chart', section: '每日情報' },
  // market-calendar 站內 iframe 頁 #/market-calendar（hr-market-calendar.jsx）同 insider
  // 保留為備援；主要入口為分類頁/工作台卡片直開新分頁（openMarketCalendarTab）。
  { key: 'market-calendar', label: '台美股市行事曆', icon: 'calendar', section: '每日情報' },
  // expert-tracker 2026-09-16 起入口改為整站代理外開（openExpertTrackerTab →
  // /api/infoscout-site/）；`open` callback 供 Cmd+K 直開新分頁。與 kelly-game
  // 不同：站內 SPA 頁 #/expert-tracker 仍在 _router.jsx ROUTE_PAGES 註冊，
  // 直連 hash 不會被彈回（router 彈回只看 ROUTE_PAGES 有無，與 `open` 無關），
  // 保留為備援、日後切回只需拿掉這裡與各入口的 open。
  { key: 'expert-tracker', label: '大神追蹤器', icon: 'activity', section: '深度研究', open: () => window.openExpertTrackerTab && window.openExpertTrackerTab() },
  { key: 'quantdata', label: '量化資料庫', icon: 'search', section: '個股與量化' },
  { key: 'tjcoach', label: '交易檢討 TJCoach', icon: 'activity', section: '個股與量化', open: () => window.openTJCoachTab && window.openTJCoachTab() },
  { key: 'insider', label: '內部人監測', icon: 'search', section: '個股與量化' },
  // txo-board 站內 iframe 頁 #/txo-board（hr-txo-board.jsx）同 insider 保留為備援；
  // 主要入口為分類頁/工作台卡片直開新分頁（openTxoBoardTab → /api/txo-board/）。
  { key: 'txo-board', label: '選擇權籌碼看板', icon: 'activity', section: '個股與量化' },
  // fund-radar 站內 iframe 頁 #/fund-radar（hr-fund-radar.jsx）同 insider 保留為備援；
  // 主要入口為分類頁/工作台卡片直開新分頁（openFundRadarTab → /api/fund-radar/）。
  { key: 'fund-radar', label: '基金持股雷達', icon: 'bar-chart', section: '個股與量化' },
  // bigday 站內 iframe 頁 #/bigday（hr-bigday.jsx）同 fund-radar 保留為備援；
  // 主要入口為分類頁/工作台卡片直開新分頁（openBigdayTab → /api/bigday/）。
  { key: 'bigday', label: 'BIGDAY 明日波動預估', icon: 'trending-up', section: '個股與量化' },
  // kelly-game 無 SPA 頁（#/kelly-game 直連會被 router 彈回）；`open` callback
  // 供 Cmd+K 直開新分頁。放行範圍比照個股工作室（analyst 兩級與 non_staff 皆可）。
  { key: 'kelly-game', label: '凱利訓練場', icon: 'activity', section: '個股與量化', open: () => window.openKellyGameTab && window.openKellyGameTab() },
  { key: 'stock-studio', label: '個股工作室', icon: 'trending-up', section: '交易' },
  // ytkb 無 SPA 頁（#/ytkb 直連會被 router 彈回 #/）；`open` callback 供
  // Cmd+K 直開新分頁（hr-cmdk.jsx activate 支援 item.open）。
  { key: 'ytkb', label: '影音知識庫', icon: 'book', section: '深度研究', open: () => window.openYtkbTab && window.openYtkbTab() },
  // research-engine 無 SPA 頁（#/research-engine 直連會被 router 彈回 #/）；
  // `open` callback 供 Cmd+K 直開新分頁。員工限定（不在非員工路由白名單，
  // 與深度研究組其他項目的放行範圍不同）。
  { key: 'research-engine', label: '研究引擎', icon: 'search', section: '深度研究', open: () => window.openResearchEngineTab && window.openResearchEngineTab() },
  // 站會帳本 / 模型評測（2026-09-15）：無 SPA 頁，`open` callback 供 Cmd+K 直開
  // 新分頁。入口在 ERP 類別選擇頁「團隊與工程」子群組（hr-category-hub.jsx），
  // section 比照 ERP 各項用子群組名（Cmd+K 副標與可搜欄位）。**員工限定**：
  // 無 roles 欄位 → 一般員工/admin 可見；不進 _ANALYST_ROUTES /
  // _ANALYST_LITE_ROUTES / _NON_STAFF_ROUTES，受限帳號在 canAccessRoute 先被
  // restrictedRouteKeys 白名單擋下（鏡射後端：/api/progress、/api/llm-judge
  // 不在任何非員工白名單）。
  { key: 'progress-ledger', label: '站會帳本', icon: 'message', section: '團隊與工程', open: () => window.openProgressLedgerTab && window.openProgressLedgerTab() },
  { key: 'llm-judge', label: '模型評測', icon: 'bar-chart', section: '團隊與工程', open: () => window.openLlmJudgeTab && window.openLlmJudgeTab() },
  // 團隊雷達（2026-09-24）：gs-team-radar（AI 用量自查交件口＋主管端開發協作能力
  // 雷達）經 portal 代理 /api/team-radar 外開，同上兩項：無 SPA 頁、員工限定、
  // 不進任何非員工路由白名單（鏡射後端：/api/team-radar 不在任何非員工白名單）。
  { key: 'team-radar', label: '團隊雷達', icon: 'activity', section: '團隊與工程', open: () => window.openTeamRadarTab && window.openTeamRadarTab() },
  // 交易部週報（gs-trader-report）：經 portal 代理 /api/trader-report 外開，同上三項：
  // 無 SPA 頁、員工限定、不進任何非員工路由白名單（鏡射後端：/api/trader-report
  // 不在任何非員工白名單）。卡片排在 ERP「團隊與工程」站會帳本旁。
  { key: 'trader-report', label: '每周稽核表', icon: 'message', section: '團隊與工程', open: () => window.openTraderReportTab && window.openTraderReportTab() },
  // 知識庫問答（gs-rag）刻意**不在**這裡 —— 2026-08-17 起改為右下浮動聊天室
  // （redesign/hr-rag-chat.jsx，掛在 _router.jsx 的全域浮層），不是路由也不是
  // 側欄項目，所以沒有 route key、不進 Cmd+K。員工限定的判定改由該元件的
  // isRestrictedAccount(user) 負責。
  { key: 'ai-center',  label: 'AI 應用工具',  icon: 'layout',      section: '工具' },
];

/* Permission helpers — single source of truth for "can this user access X" logic. */
function userRoles(user) {
  if (!user) return [];
  const arr = user.roles || [];
  // include legacy users.role single field for backwards compat
  if (user.role && !arr.includes(user.role)) return [...arr, user.role];
  return arr;
}
function isAdmin(user) { return userRoles(user).includes('admin'); }
function hasRole(user, ...roles) {
  const r = userRoles(user);
  return roles.some(role => r.includes(role));
}

/* 外部分析帳號（非員工）— 只持 analyst 角色、不持任何員工/管理角色。
 * 對應後端 main.py AnalystGuardMiddleware 的判定。analyst-only 使用者只能用
 * 「分析工具」整區（research / files）+ 個股工作室，登入後直接落地在個股工作室
 * （2026-08-13 起，原 Deep Research）。 */
const _PRIVILEGED_ROLES = ['admin', 'hr', 'finance', 'manager', 'trade_editor', 'employee'];
/* 外部分析家族角色 — analyst（高級，可下載報告）+ analyst_lite（一般，不可下載）。
 * 兩者 UI 降級行為相同，差別在下載權限（由 ocr_report.download capability 判定）
 * 以及量化資料庫（analyst_lite 不可用，見 _ANALYST_LITE_ROUTES）。 */
const _ANALYST_ROLE_CODES = ['analyst', 'analyst_lite'];
const _ANALYST_ROUTES = ['hub', 'research', 'files', 'daily-brief', 'analysis-report', 'morning-report', 'morning-focus', 'market-calendar', 'expert-tracker', 'quantdata', 'insider', 'txo-board', 'fund-radar', 'bigday', 'ytkb', 'stock-studio', 'kelly-game', 'tjcoach'];
/* analyst_lite（一般外部分析）路由白名單 — analyst 白名單扣掉量化資料庫，
 * 對齊後端 _ANALYST_LITE_ALLOW_PREFIXES（main.py，不含 /api/quantdata）。
 * 'hub'（分析工作台）隨衍生寫法自動帶入兩級。 */
const _ANALYST_LITE_ROUTES = _ANALYST_ROUTES.filter(k => k !== 'quantdata');
/* 一般非員工帳號（non_staff）路由白名單 — 側欄「分析工具」+「工具」兩區全部，
 * 對齊後端 non_staff 閘門 `_NON_STAFF_ALLOW_PREFIXES`（main.py）：
 *   research        → /api/research + /api/tasks（Deep Research）
 *   files           → /api/files（知識庫）
 *   daily-brief     → /api/news（每日重點資訊）
 *   analysis-report → /api/ocr-reports（分析報告）
 *   morning-report  → /api/ocr-reports（早晨報告 — 早晨上傳者的報告，共用同一 API 依 uploader 分流）
 *   morning-focus   → /api/morning-focus（早晨重點 — 華南投顧盤前多空新聞重排版；後端另行實作）
 *   market-calendar → /api/market-calendar（台美股市行事曆 — 代理 192.168.11.90:8813 Vite SPA，直開新分頁）
 *   expert-tracker  → /api/infoscout（大神追蹤器 — 代理內網 InfoScout GB10）
 *   quantdata       → /api/quantdata（量化資料庫 — 代理內網 QUANTDATA Search）
 *   insider         → /api/insider（內部人監測 — 代理 insider.gsinvest.com 靜態站）
 *   txo-board       → /api/txo-board（台指選擇權籌碼看板 — 代理 192.168.11.33:8790 單頁看板，直開新分頁）
 *   fund-radar      → /api/fund-radar（基金持股雷達 — 代理 192.168.11.33:8050 SPA，直開新分頁）
 *   bigday          → /api/bigday（BIGDAY 明日波動預估 — 代理 192.168.11.90:8655 Flask dashboard，唯讀，直開新分頁）
 *   ytkb            → /api/ytkb（影音知識庫 — 代理內網 GB10 YTKB 工作台，直開新分頁）
 *   ai-center       → 純靜態展示頁，無 /api 呼叫
 *   lunch           → /api/lunch（午餐訂購）
 *   courses         → /api/courses（教學）
 *   dashboard       → /api/positions + /api/market（交易紀錄）
 *   stock-studio    → /api/finlab（個股工作室 — 個股行情/估值/籌碼/報告；後端待接）
 *   kelly-game      → /api/kelly（凱利下注訓練場 — 代理內網 GB10 kelly-game 靜態遊戲，直開新分頁）
 *   hub             → /api/tools（分析工作台 — 全帳號共用工作台，2026-08-24 起）
 * dashboard 進白名單後，non_staff 開 #/ 直接落地交易紀錄頁；
 * 越權路由導向 #/hub 分析工作台（見 _router.jsx，2026-08-24 起與 analyst 統一，
 * 原導 #/daily-brief）。 */
const _NON_STAFF_ROUTES = ['hub', 'research', 'files', 'daily-brief', 'analysis-report', 'morning-report', 'morning-focus', 'market-calendar', 'expert-tracker', 'quantdata', 'insider', 'txo-board', 'fund-radar', 'bigday', 'ytkb', 'ai-center', 'lunch', 'courses', 'dashboard', 'stock-studio', 'kelly-game', 'tjcoach'];

function _hasPrivilegedRole(user) {
  const r = userRoles(user);
  return _PRIVILEGED_ROLES.some(p => r.includes(p));
}
/* 注意：analyst+non_staff 混搭（無 privileged role）也算 analyst-only ——
 * 沿用 analyst 的 UI 降級（隱藏行情 ticker / 右側看板 / 交易紀錄），
 * 路由白名單另由 restrictedRouteKeys() 取聯集。 */
function isAnalystOnly(user) {
  const r = userRoles(user);
  return _ANALYST_ROLE_CODES.some(c => r.includes(c)) && !_hasPrivilegedRole(user);
}
/* 純 non_staff 帳號 — 持 non_staff、不持任何 analyst 家族角色、無任何員工/管理角色。 */
function isNonStaffOnly(user) {
  const r = userRoles(user);
  return r.includes('non_staff') && !_ANALYST_ROLE_CODES.some(c => r.includes(c)) && !_hasPrivilegedRole(user);
}
/* 受限帳號的路由白名單 — analyst 家族 / non_staff（無任何 privileged role）：
 *   analyst（高級）持有      → 展開 _ANALYST_ROUTES
 *   analyst_lite 持有        → 展開 _ANALYST_LITE_ROUTES（不含 quantdata）
 *   non_staff 持有           → 展開 _NON_STAFF_ROUTES
 * 多角色混搭取聯集（對齊後端「各角色白名單聯集」——例如 analyst_lite+non_staff
 * 因 non_staff 而仍可用量化資料庫，屬預期行為）。
 * 一般帳號（有 privileged role 或無非員工角色）回傳 null = 不受限。 */
function restrictedRouteKeys(user) {
  if (_hasPrivilegedRole(user)) return null;
  const r = userRoles(user);
  const analyst = r.includes('analyst');
  const analystLite = r.includes('analyst_lite');
  const nonStaff = r.includes('non_staff');
  if (!analyst && !analystLite && !nonStaff) return null;
  return [
    ...(analyst ? _ANALYST_ROUTES : []),
    ...(analystLite ? _ANALYST_LITE_ROUTES : []),
    ...(nonStaff ? _NON_STAFF_ROUTES : []),
  ];
}
function isRestrictedAccount(user) { return restrictedRouteKeys(user) !== null; }

/* 側欄「工具」區外部連結（GitHub 等，無對應 route key、不打本站後端）可見性 —
 * 一般帳號可見；non_staff（含 analyst+non_staff 混搭）工具區整區開放 → 可見；
 * analyst-only 僅分析工具白名單 → 隱藏。 */
function canAccessToolLinks(user) {
  return !isRestrictedAccount(user) || userRoles(user).includes('non_staff');
}

function canAccessRoute(user, routeKey) {
  if (routeKey === 'updates') return !!user;
  // 受限帳號（analyst / non_staff / 兩者混搭）：白名單之外一律拒絕（先判，蓋過下方 generic 規則）。
  const restricted = restrictedRouteKeys(user);
  if (restricted) return restricted.includes(routeKey);
  // Look in both ROUTES (sidebar) and EXTRA_ROUTES (Cmd+K / direct-URL)
  const route = ROUTES.find(r => r.key === routeKey)
             || EXTRA_ROUTES.find(r => r.key === routeKey);
  if (!route) return false;
  if (isAdmin(user)) return true;          // admin sees all
  if (!route.roles) return true;           // no roles defined = everyone
  if (route.roles.length === 0) return false; // empty list = admin only
  return route.roles.some(r => userRoles(user).includes(r));
}

/* Capability check — finer-grained than role check, mirrors backend.permissions.
 * Backend returns `capabilities: list[str]` on /api/auth/me (union from user's roles).
 * Use this for in-page button / tab visibility:
 *   {hasCapability(user, 'payroll.pay') && <Button>...</Button>}
 *
 * Note: backend `require_role()` still enforces at API level — capability is
 * just for UI rendering. Don't rely on it for security.
 *
 * admin bypass: admin role always returns true (covers older tokens without
 * capabilities field yet, though /me now always sends it). */
function hasCapability(user, capability) {
  if (!user) return false;
  if (isAdmin(user)) return true;
  const caps = user.capabilities || [];
  return caps.includes(capability);
}

/* Multi-capability check — true if user has ANY of the listed capabilities. */
function hasAnyCapability(user, ...capabilities) {
  if (!user) return false;
  if (isAdmin(user)) return true;
  const caps = user.capabilities || [];
  return capabilities.some(c => caps.includes(c));
}

/* ─────────────  外開新分頁 — 品牌化過場頁（共用）  ─────────────
 * open*Tab 系 handler 在背景換 session cookie 期間，新分頁先顯示這頁
 * 站內品牌視覺的過場（比照 login.html / 側欄品牌區：米白底 #F6F4EF、
 * 墨字 #0E1411、Genesis 方形 G logo、serif 目的地名稱 + 細脈動進度條）。
 * 帶 errorMsg 時改渲染同視覺的失敗訊息（磚紅 #9B3330、無進度條），
 * 分頁保留給使用者自行關閉。全部 inline HTML/CSS、不放外部資源
 * （about:blank 文件載不到站內字型，serif 落到 Georgia fallback）。 */
function _extTabInterstitialHtml(destName, errorMsg) {
  const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const serif = "'Source Serif 4',Georgia,serif";
  const status = errorMsg
    ? '<div style="font-size:13px;color:#9B3330;letter-spacing:.04em;max-width:420px;' +
      'margin:0 auto;line-height:1.7;">' + esc(errorMsg) + '</div>' +
      '<div style="font-size:11px;color:#8B8F89;letter-spacing:.08em;margin-top:12px;">' +
      '可關閉此分頁後重試</div>'
    : '<div style="font-size:13px;color:#4A5048;letter-spacing:.06em;">正在連線至外部服務…</div>' +
      '<div style="width:120px;height:2px;background:#E5E2DA;border-radius:99px;' +
      'margin:18px auto 0;overflow:hidden;">' +
      '<div style="width:40%;height:100%;background:#0B5F5A;border-radius:99px;' +
      'animation:gsSlide 1.2s ease-in-out infinite;"></div></div>';
  return (
    '<!doctype html><html lang="zh-Hant"><head><meta charset="utf-8">' +
    '<title>' + esc(destName) + '</title>' +
    '<style>@keyframes gsSlide{0%{transform:translateX(-100%)}100%{transform:translateX(320%)}}</style>' +
    '</head>' +
    '<body style="margin:0;height:100vh;display:grid;place-items:center;' +
    'background:#F6F4EF;color:#0E1411;' +
    "font-family:'Inter','Helvetica Neue','Noto Sans TC',system-ui,sans-serif;\">" +
    '<div style="text-align:center;padding:0 24px;">' +
    '<div style="width:44px;height:44px;border-radius:9px;background:#0B5F5A;color:#fff;' +
    'display:grid;place-items:center;margin:0 auto 18px;' +
    'font-family:' + serif + ';font-weight:600;font-size:21px;letter-spacing:-0.02em;' +
    'box-shadow:0 4px 12px rgba(11,95,90,0.18);">G</div>' +
    '<div style="font-size:11px;letter-spacing:.24em;text-transform:uppercase;' +
    'color:#8B8F89;margin-bottom:10px;">Genesis Portal</div>' +
    '<div style="font-family:' + serif + ';font-size:26px;font-weight:500;' +
    'letter-spacing:-0.01em;margin-bottom:14px;">' + esc(destName) + '</div>' +
    status +
    '</div></body></html>'
  );
}
/* 把過場（或失敗訊息）寫進新分頁。寫不進去（跨源/已導向等）不影響主流程。 */
function _writeExtTabInterstitial(win, destName, errorMsg) {
  try {
    win.document.open();
    win.document.write(_extTabInterstitialHtml(destName, errorMsg));
    win.document.close();
  } catch (e) { /* 過場寫不進去不影響主流程 */ }
}

/* ─────────────  量化資料庫 — 側欄「直開新分頁」handler  ─────────────
 * 側欄點「量化資料庫」不走 SPA 路由，直接在新分頁開代理整站 /api/quantdata/：
 *   1. 必須在 click 手勢堆疊內「同步」window.open，否則被 popup blocker 擋
 *      → 先開 about:blank 拿分頁引用，寫一頁極簡「連線中…」中間態；
 *   2. 背景 POST /api/quantdata/session（api() 帶 Bearer JWT）換 HttpOnly
 *      qd_session cookie（Path=/api/quantdata，見 routes_quantdata.py）；
 *   3. 成功 → 新分頁導向 /api/quantdata/（cookie 已落地，整站可瀏覽）；
 *      失敗 → 關掉新分頁 + toast 報錯（無 toast context 時 fallback console）。
 * #/quantdata 站內 iframe 頁（hr-quantdata.jsx）保留作 deep-link / Cmd+K 後備。
 * 權限判定仍走 canAccessRoute('quantdata')（analyst 白名單內）——
 * 勿改用 canAccessToolLinks（analyst-only 看不到工具區外連，但必須看得到本項）。 */
function openQuantDataTab(toast) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    else console.error('openQuantDataTab: popup blocked');
    return;
  }
  _writeExtTabInterstitial(win, '量化資料庫');
  api('POST', '/quantdata/session')
    .then(() => { win.location.replace('/api/quantdata/'); })
    .catch((e) => {
      const msg = (e && e.detail) ? `量化資料庫連線失敗：${e.detail}` : '量化資料庫連線失敗，請稍後重試';
      _writeExtTabInterstitial(win, '量化資料庫', msg);
      if (toast) toast(msg, 'error');
      else console.error('openQuantDataTab:', msg);
    });
}

/* ─────────────  內部人監測 — 側欄「直開新分頁」handler  ─────────────
 * 邏輯照 openQuantDataTab：同步 window.open 過 popup blocker → 背景
 * POST /api/insider/session 換 HttpOnly insider_session cookie
 * （Path=/api/insider，見 routes_insider.py）→ 成功導向 /api/insider/、
 * 失敗關分頁 + toast 報錯。#/insider 站內 iframe 頁（hr-insider.jsx）保留作
 * deep-link / Cmd+K 後備。權限判定仍走 canAccessRoute('insider')
 * （analyst 白名單內）—— 勿改用 canAccessToolLinks。 */
function openInsiderTab(toast) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    else console.error('openInsiderTab: popup blocked');
    return;
  }
  _writeExtTabInterstitial(win, '內部人監測');
  api('POST', '/insider/session')
    .then(() => { win.location.replace('/api/insider/'); })
    .catch((e) => {
      const msg = (e && e.detail) ? `內部人監測連線失敗：${e.detail}` : '內部人監測連線失敗，請稍後重試';
      _writeExtTabInterstitial(win, '內部人監測', msg);
      if (toast) toast(msg, 'error');
      else console.error('openInsiderTab:', msg);
    });
}

/* ─────────────  台指選擇權籌碼看板 — 「直開新分頁」handler  ─────────────
 * 邏輯照 openInsiderTab：同步 window.open 過 popup blocker → 背景
 * POST /api/txo-board/session 換 HttpOnly txo_board_session cookie
 * （Path=/api/txo-board，見 routes_txo_board.py）→ 成功導向 /api/txo-board/、
 * 失敗過場頁顯示錯誤 + toast。#/txo-board 站內 iframe 頁（hr-txo-board.jsx）
 * 保留作 deep-link / Cmd+K 後備。權限判定仍走 canAccessRoute('txo-board')
 * （analyst 兩級與 non_staff 白名單內，同 insider）—— 勿改用 canAccessToolLinks。 */
function openTxoBoardTab(toast) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    else console.error('openTxoBoardTab: popup blocked');
    return;
  }
  _writeExtTabInterstitial(win, '選擇權籌碼看板');
  api('POST', '/txo-board/session')
    .then(() => { win.location.replace('/api/txo-board/'); })
    .catch((e) => {
      const msg = (e && e.detail) ? `選擇權籌碼看板連線失敗：${e.detail}` : '選擇權籌碼看板連線失敗，請稍後重試';
      _writeExtTabInterstitial(win, '選擇權籌碼看板', msg);
      if (toast) toast(msg, 'error');
      else console.error('openTxoBoardTab:', msg);
    });
}

/* ─────────────  台美股市行事曆 — 「直開新分頁」handler  ─────────────
 * 邏輯照 openInsiderTab / openTxoBoardTab：同步 window.open 過 popup blocker →
 * 背景 POST /api/market-calendar/session 換 HttpOnly market_calendar_session
 * cookie（Path=/api/market-calendar，見 routes_market_calendar.py）→ 成功導向
 * /api/market-calendar/（上游 Vite SPA，資產與 /api/events 由代理改寫）、
 * 失敗過場頁顯示錯誤 + toast。#/market-calendar 站內 iframe 頁
 * （hr-market-calendar.jsx）保留作 deep-link / Cmd+K 後備。
 * 權限判定仍走 canAccessRoute('market-calendar')（analyst 兩級與 non_staff
 * 白名單內，同 insider）—— 勿改用 canAccessToolLinks。 */
function openMarketCalendarTab(toast) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    else console.error('openMarketCalendarTab: popup blocked');
    return;
  }
  _writeExtTabInterstitial(win, '台美股市行事曆');
  api('POST', '/market-calendar/session')
    .then(() => { win.location.replace('/api/market-calendar/'); })
    .catch((e) => {
      const msg = (e && e.detail) ? `台美股市行事曆連線失敗：${e.detail}` : '台美股市行事曆連線失敗，請稍後重試';
      _writeExtTabInterstitial(win, '台美股市行事曆', msg);
      if (toast) toast(msg, 'error');
      else console.error('openMarketCalendarTab:', msg);
    });
}

/* ─────────────  基金持股雷達 — 「直開新分頁」handler  ─────────────
 * 邏輯照 openMarketCalendarTab：同步 window.open 過 popup blocker →
 * 背景 POST /api/fund-radar/session 換 HttpOnly fund_radar_session cookie
 * （Path=/api/fund-radar，見 routes_fund_radar.py）→ 成功導向 /api/fund-radar/
 * （上游 SPA，/static 資產與 /api/* 由代理改寫）、失敗過場頁顯示錯誤 + toast。
 * #/fund-radar 站內 iframe 頁（hr-fund-radar.jsx）保留作 deep-link / Cmd+K 後備。
 * 權限判定仍走 canAccessRoute('fund-radar')（analyst 兩級與 non_staff
 * 白名單內，同 insider）—— 勿改用 canAccessToolLinks。 */
function openFundRadarTab(toast) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    else console.error('openFundRadarTab: popup blocked');
    return;
  }
  _writeExtTabInterstitial(win, '基金持股雷達');
  api('POST', '/fund-radar/session')
    .then(() => { win.location.replace('/api/fund-radar/'); })
    .catch((e) => {
      const msg = (e && e.detail) ? `基金持股雷達連線失敗：${e.detail}` : '基金持股雷達連線失敗，請稍後重試';
      _writeExtTabInterstitial(win, '基金持股雷達', msg);
      if (toast) toast(msg, 'error');
      else console.error('openFundRadarTab:', msg);
    });
}

/* ─────────────  BIGDAY 明日波動預估 — 「直開新分頁」handler  ─────────────
 * 邏輯照 openMarketCalendarTab / openFundRadarTab：同步 window.open 過 popup
 * blocker → 背景 POST /api/bigday/session 換 HttpOnly bigday_session cookie
 * （Path=/api/bigday，見 routes_bigday.py）→ 成功導向 /api/bigday/（上游 Flask
 * dashboard，根絕對頁面/資產/API 由代理改寫、只放行 GET/HEAD）、失敗過場頁顯示
 * 錯誤 + toast。#/bigday 站內 iframe 頁（hr-bigday.jsx）保留作 deep-link /
 * Cmd+K 後備。權限判定仍走 canAccessRoute('bigday')（analyst 兩級與 non_staff
 * 白名單內，同 market-calendar）—— 勿改用 canAccessToolLinks。 */
function openBigdayTab(toast) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    else console.error('openBigdayTab: popup blocked');
    return;
  }
  _writeExtTabInterstitial(win, 'BIGDAY 明日波動預估');
  api('POST', '/bigday/session')
    .then(() => { win.location.replace('/api/bigday/'); })
    .catch((e) => {
      const msg = (e && e.detail) ? `BIGDAY 明日波動預估連線失敗：${e.detail}` : 'BIGDAY 明日波動預估連線失敗，請稍後重試';
      _writeExtTabInterstitial(win, 'BIGDAY 明日波動預估', msg);
      if (toast) toast(msg, 'error');
      else console.error('openBigdayTab:', msg);
    });
}

/* ─────────────  YTKB — AI 應用工具卡片「直開新分頁」handler  ─────────────
 * 邏輯照 openQuantDataTab：同步 window.open 過 popup blocker → 背景
 * POST /api/ytkb/session 換 HttpOnly ytkb_session cookie
 * （Path=/api/ytkb，見 routes_ytkb.py）→ 成功導向 /api/ytkb/dashboard/
 * （YTKB 工作台 SPA 入口）、失敗關分頁 + toast 報錯。
 * 入口：側欄「分析工具」區項目（variant-a-v2.jsx）+ AI 應用工具頁卡片
 * （hr-ai-center.jsx）+ Cmd+K（EXTRA_ROUTES open callback）。無 iframe
 * 後備頁；YTKB 自己另有站內登入（上游 token），與 portal 認證無關。 */
function openYtkbTab(toast) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    else console.error('openYtkbTab: popup blocked');
    return;
  }
  _writeExtTabInterstitial(win, '影音知識庫');
  api('POST', '/ytkb/session')
    .then(() => { win.location.replace('/api/ytkb/dashboard/'); })
    .catch((e) => {
      const msg = (e && e.detail) ? `影音知識庫連線失敗：${e.detail}` : '影音知識庫連線失敗，請稍後重試';
      _writeExtTabInterstitial(win, '影音知識庫', msg);
      if (toast) toast(msg, 'error');
      else console.error('openYtkbTab:', msg);
    });
}

/* ─────────────  研究引擎 — 側欄「直開新分頁」handler  ─────────────
 * 邏輯照 openQuantDataTab：同步 window.open 過 popup blocker → 背景
 * POST /api/research-engine/session 換 HttpOnly re_session cookie
 * （Path=/api/research-engine，見 routes_research_engine.py）→ 成功導向
 * /api/research-engine/（HSG 研究引擎首頁）、失敗關分頁 + toast 報錯。
 * 入口：側欄「分析工具」區項目（variant-a-v2.jsx）+ Cmd+K（EXTRA_ROUTES
 * open callback）+ 大神追蹤器「深入研究」modal 的「在研究引擎開啟」
 * （hr-expert-tracker.jsx，帶 query）。無 iframe 後備頁；員工限定
 * （不在非員工路由白名單，後端閘門同步 default-deny——與分析工具區
 * 其他項目的放行範圍不同）。
 * @param query 選填 — 帶上時以 ?q=<主題> 深連結，HSG 頁預填研究主題
 *   textarea（上游 index.html 的 URLSearchParams 支援；上限對齊上游
 *   DeepRequest.query 的 500 字）。 */
function openResearchEngineTab(toast, query) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    else console.error('openResearchEngineTab: popup blocked');
    return;
  }
  _writeExtTabInterstitial(win, '研究引擎');
  const target = '/api/research-engine/'
    + (query ? `?q=${encodeURIComponent(String(query).slice(0, 500))}` : '');
  api('POST', '/research-engine/session')
    .then(() => { win.location.replace(target); })
    .catch((e) => {
      const msg = (e && e.detail) ? `研究引擎連線失敗：${e.detail}` : '研究引擎連線失敗，請稍後重試';
      _writeExtTabInterstitial(win, '研究引擎', msg);
      if (toast) toast(msg, 'error');
      else console.error('openResearchEngineTab:', msg);
    });
}

/* ─────────────  凱利下注訓練場 — 側欄「直開新分頁」handler  ─────────────
 * 邏輯照 openQuantDataTab：同步 window.open 過 popup blocker → 背景
 * POST /api/kelly/session 換 HttpOnly kelly_session cookie
 * （Path=/api/kelly，見 routes_kelly.py）→ 成功導向 /api/kelly/
 * （**尾斜線必須**——上游 index.html 全用相對路徑資產，缺尾斜線時
 * styles.css 會解析成 /api/styles.css）、失敗留過場報錯 + toast。
 * 入口：側欄「個股與量化」區項目（variant-a-v2.jsx）+ 分析工作台卡片
 * （hr-analyst-hub.jsx）+ Cmd+K（EXTRA_ROUTES open callback）。
 * 無站內 iframe 後備頁（#/kelly-game 會被 router 彈回）。
 * 權限走 canAccessRoute('kelly-game')（analyst 兩級與 non_staff 皆放行，
 * 比照個股工作室）。 */
function openKellyGameTab(toast) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    else console.error('openKellyGameTab: popup blocked');
    return;
  }
  _writeExtTabInterstitial(win, '凱利訓練場');
  api('POST', '/kelly/session')
    .then(() => { win.location.replace('/api/kelly/'); })
    .catch((e) => {
      const msg = (e && e.detail) ? `凱利訓練場連線失敗：${e.detail}` : '凱利訓練場連線失敗，請稍後重試';
      _writeExtTabInterstitial(win, '凱利訓練場', msg);
      if (toast) toast(msg, 'error');
      else console.error('openKellyGameTab:', msg);
    });
}

/* ─────────────  大神追蹤器 — 直開新分頁 handler（整站代理外開）  ─────────────
 * 2026-09-16 起大神追蹤器改為「整站反向代理外開」：同 openKellyGameTab，
 * 同步 window.open 過 popup blocker → 背景 POST /api/infoscout-site/session
 * 換 HttpOnly infoscout_session cookie（Path=/api/infoscout-site，見
 * routes_infoscout_site.py）→ 成功導向 /api/infoscout-site/（**尾斜線必須**
 * ——上游 InfoScout 網站相對路徑資產需要它）、失敗留過場報錯 + toast。
 * 入口：類別選擇頁「深度研究」卡（hr-category-hub.jsx）+ 分析工作台卡片
 * （hr-analyst-hub.jsx，/api/tools manifest mode=tab）+ Cmd+K（EXTRA_ROUTES
 * open callback）。
 * 站內 SPA 頁 #/expert-tracker（hr-expert-tracker.jsx，打 /api/infoscout/*）
 * **保留為備援**：_router.jsx ROUTE_PAGES 註冊不動，可由直連 hash 進入；
 * 日後要切回站內版只需把上述入口改回 hash，不必動頁面本身。
 * 權限沿用 canAccessRoute('expert-tracker')（analyst 兩級與 non_staff 皆
 * 放行，後端 /api/infoscout-site 白名單範圍同 /api/infoscout）。 */
function openExpertTrackerTab(toast) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    else console.error('openExpertTrackerTab: popup blocked');
    return;
  }
  _writeExtTabInterstitial(win, '大神追蹤器');
  api('POST', '/infoscout-site/session')
    .then(() => { win.location.replace('/api/infoscout-site/'); })
    .catch((e) => {
      const msg = (e && e.detail) ? `大神追蹤器連線失敗：${e.detail}` : '大神追蹤器連線失敗，請稍後重試';
      _writeExtTabInterstitial(win, '大神追蹤器', msg);
      if (toast) toast(msg, 'error');
      else console.error('openExpertTrackerTab:', msg);
    });
}

/* ─────────────  軟體發佈中心 — 直開新分頁 handler  ─────────────
 * 同 openKellyGameTab：同步 window.open 過 popup blocker → 背景
 * POST /api/release-hub/session 換 HttpOnly release_hub_session cookie
 * （Path=/api/release-hub，見 routes_release_hub.py）→ 成功導向
 * /api/release-hub/、失敗留過場報錯 + toast。上游 gs-release-hub 全用根
 * 絕對路徑（/api/theme.css、fetch('/api/index')、/download/...），由代理端
 * 改寫掛前綴，前端不用管。
 * 入口：**只有**側欄「工具」區項目（variant-a-v2.jsx）—— 無 route key、
 * 不進 Cmd+K、不進 /api/tools manifest（manifest 無「工具」分類）。
 * 可見性走 canAccessToolLinks（員工 + non_staff 可見、analyst-only 隱藏），
 * 後端白名單鏡射：/api/release-hub 只在 _NON_STAFF_ALLOW_PREFIXES、不在
 * analyst 兩級白名單。 */
function openReleaseHubTab(toast) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    else console.error('openReleaseHubTab: popup blocked');
    return;
  }
  _writeExtTabInterstitial(win, '軟體發佈中心');
  api('POST', '/release-hub/session')
    .then(() => { win.location.replace('/api/release-hub/'); })
    .catch((e) => {
      const msg = (e && e.detail) ? `軟體發佈中心連線失敗：${e.detail}` : '軟體發佈中心連線失敗，請稍後重試';
      _writeExtTabInterstitial(win, '軟體發佈中心', msg);
      if (toast) toast(msg, 'error');
      else console.error('openReleaseHubTab:', msg);
    });
}

/* ─────────────  站會帳本 — 直開新分頁 handler  ─────────────
 * 同 openReleaseHubTab：同步 window.open 過 popup blocker → 背景
 * POST /api/progress/session 換 HttpOnly progress_session cookie
 * （Path=/api/progress，見 routes_progress.py）→ 成功導向 /api/progress/
 * （**尾斜線必須**——上游 inline JS 用相對路徑 fetch("api/data")，缺尾斜線
 * 會解析成 /api/api/data）、失敗留過場報錯 + toast。上游不需改寫。
 * 入口：ERP 類別選擇頁「團隊與工程」卡片（hr-category-hub.jsx）+ Cmd+K
 * （EXTRA_ROUTES open callback）；不進 /api/tools manifest。
 * 可見性走 canAccessRoute('progress-ledger')（**員工限定**：route key 不在
 * 任何非員工路由白名單），後端白名單鏡射：/api/progress 不在
 * _ANALYST_ALLOW_PREFIXES / _NON_STAFF_ALLOW_PREFIXES 任何一份。 */
function openProgressLedgerTab(toast) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    else console.error('openProgressLedgerTab: popup blocked');
    return;
  }
  _writeExtTabInterstitial(win, '站會帳本');
  api('POST', '/progress/session')
    .then(() => { win.location.replace('/api/progress/'); })
    .catch((e) => {
      const msg = (e && e.detail) ? `站會帳本連線失敗：${e.detail}` : '站會帳本連線失敗，請稍後重試';
      _writeExtTabInterstitial(win, '站會帳本', msg);
      if (toast) toast(msg, 'error');
      else console.error('openProgressLedgerTab:', msg);
    });
}

/* ─────────────  模型評測（llm-judge）— 直開新分頁 handler  ─────────────
 * 同 openReleaseHubTab：同步 window.open 過 popup blocker → 背景
 * POST /api/llm-judge/session 換 HttpOnly llm_judge_session cookie
 * （Path=/api/llm-judge，見 routes_llm_judge.py）→ 成功導向 /api/llm-judge/
 * （尾斜線必須——gs-glossary.css/.js 與 glossary.json 為相對路徑）、失敗留
 * 過場報錯 + toast。上游 inline JS 的根絕對 fetch("/api/runs") 等由代理端
 * 改寫掛前綴，前端不用管。
 * 入口：ERP 類別選擇頁「團隊與工程」卡片（hr-category-hub.jsx）+ Cmd+K
 * （EXTRA_ROUTES open callback）；不進 /api/tools manifest。
 * 可見性走 canAccessRoute('llm-judge')（**員工限定**：route key 不在任何
 * 非員工路由白名單），後端白名單鏡射：/api/llm-judge 不在
 * _ANALYST_ALLOW_PREFIXES / _NON_STAFF_ALLOW_PREFIXES 任何一份。 */
function openLlmJudgeTab(toast) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    else console.error('openLlmJudgeTab: popup blocked');
    return;
  }
  _writeExtTabInterstitial(win, '模型評測');
  api('POST', '/llm-judge/session')
    .then(() => { win.location.replace('/api/llm-judge/'); })
    .catch((e) => {
      const msg = (e && e.detail) ? `模型評測連線失敗：${e.detail}` : '模型評測連線失敗，請稍後重試';
      _writeExtTabInterstitial(win, '模型評測', msg);
      if (toast) toast(msg, 'error');
      else console.error('openLlmJudgeTab:', msg);
    });
}

/* ─────────────  團隊雷達（gs-team-radar）— 直開新分頁 handler  ─────────────
 * 同 openLlmJudgeTab：同步 window.open 過 popup blocker → 背景
 * POST /api/team-radar/session 換 HttpOnly team_radar_session cookie
 * （Path=/api/team-radar，見 routes_team_radar.py）→ 成功導向 /api/team-radar/
 * （**尾斜線必須**——裸前綴由代理 307 補斜線，直接帶斜線省一跳）、失敗留
 * 過場報錯 + toast。上游全用根絕對路徑（/api/portal/*、/api/admin/*、
 * /downloads/*、/admin/login）由代理端 bytes 改寫掛前綴，前端不用管。
 * 落地是公開的「AI 用量自查 · 交件口」；主管端「開發協作能力雷達」在站內
 * /admin/ 另以上游自己的密碼登入（上游 Set-Cookie 由代理改 Path 透傳）。
 * 入口：ERP 類別選擇頁「團隊與工程」卡片（hr-category-hub.jsx）+ Cmd+K
 * （EXTRA_ROUTES open callback）；不進 /api/tools manifest。
 * 可見性走 canAccessRoute('team-radar')（**員工限定**：route key 不在任何
 * 非員工路由白名單），後端白名單鏡射：/api/team-radar 不在
 * _ANALYST_ALLOW_PREFIXES / _NON_STAFF_ALLOW_PREFIXES 任何一份。 */
function openTeamRadarTab(toast) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    else console.error('openTeamRadarTab: popup blocked');
    return;
  }
  _writeExtTabInterstitial(win, '團隊雷達');
  api('POST', '/team-radar/session')
    .then(() => { win.location.replace('/api/team-radar/'); })
    .catch((e) => {
      const msg = (e && e.detail) ? `團隊雷達連線失敗：${e.detail}` : '團隊雷達連線失敗，請稍後重試';
      _writeExtTabInterstitial(win, '團隊雷達', msg);
      if (toast) toast(msg, 'error');
      else console.error('openTeamRadarTab:', msg);
    });
}

/* ─────────────  交易部週報（gs-trader-report）— 直開新分頁 handler  ─────────────
 * 同 openProgressLedgerTab：同步 window.open 過 popup blocker → 背景
 * POST /api/trader-report/session 換 HttpOnly trader_report_session cookie
 * （Path=/api/trader-report，見 routes_trader_report.py）→ 成功導向
 * /api/trader-report/（**尾斜線必須**——上游 SPA 以 /api/trader-report/ 為 base、
 * 資產與 API 皆相對 base）、失敗留過場報錯 + toast。上游不需改寫；代理對上游
 * 注入呼叫者的 portal JWT（上游轉打 /api/auth/me 辨識使用者）。
 * 入口：ERP 類別選擇頁「團隊與工程」卡片（hr-category-hub.jsx）+ Cmd+K
 * （EXTRA_ROUTES open callback）；不進 /api/tools manifest。
 * 可見性走 canAccessRoute('trader-report')（**員工限定**：route key 不在任何
 * 非員工路由白名單），後端白名單鏡射：/api/trader-report 不在
 * _ANALYST_ALLOW_PREFIXES / _NON_STAFF_ALLOW_PREFIXES 任何一份。 */
function openTraderReportTab(toast) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    else console.error('openTraderReportTab: popup blocked');
    return;
  }
  _writeExtTabInterstitial(win, '每周稽核表');
  api('POST', '/trader-report/session')
    .then(() => { win.location.replace('/api/trader-report/'); })
    .catch((e) => {
      const msg = (e && e.detail) ? `每周稽核表連線失敗：${e.detail}` : '每周稽核表連線失敗，請稍後重試';
      _writeExtTabInterstitial(win, '每周稽核表', msg);
      if (toast) toast(msg, 'error');
      else console.error('openTraderReportTab:', msg);
    });
}

/* TJCoach 使用一次性 token：先在入口網換取短效簽章，再由新分頁以表單 POST
 * 到 TJCoach /auth/portal。token 不放網址，TJCoach 自行建立登入 session。 */
function openTJCoachTab(toast) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    return;
  }
  _writeExtTabInterstitial(win, '交易檢討 TJCoach');
  api('POST', '/tjcoach/handoff')
    .then(({ handoff_url, token }) => {
      if (!handoff_url || !token || win.closed) throw new Error('TJCoach 登入資訊不完整');
      const form = win.document.createElement('form');
      form.method = 'POST';
      form.action = handoff_url;
      const field = win.document.createElement('input');
      field.type = 'hidden';
      field.name = 'token';
      field.value = token;
      form.appendChild(field);
      win.document.body.appendChild(form);
      win.opener = null;
      form.submit();
    })
    .catch((e) => {
      const msg = (e && (e.detail || e.message)) ? `TJCoach 連線失敗：${e.detail || e.message}` : 'TJCoach 連線失敗，請稍後重試';
      if (!win.closed) _writeExtTabInterstitial(win, '交易檢討 TJCoach', msg);
      if (toast) toast(msg, 'error');
      else console.error('openTJCoachTab:', msg);
    });
}

/* ─────────────  台股掃描器 — URL 設定化 + 直開新分頁 handler  ─────────────
 * 掃描器外連網址不再由前端硬編：模組載入後首次呼叫 getScannerUrl() 時
 * lazy fetch GET /api/tools（routes_tools.py manifest，任何登入角色都拿得到）
 * 一次並快取 promise；讀 manifest 中 key='scanner' 的 external_url
 * （後端可用 SCANNER_URL 環境變數覆寫）。401/失敗/查無 → 回 fallback
 * 舊硬編 URL，永不 reject。 */
const _SCANNER_FALLBACK_URL = 'https://alethia-unservable-detailedly.ngrok-free.dev/position-vote';
let _scannerUrlPromise = null;
function getScannerUrl() {
  if (!_scannerUrlPromise) {
    _scannerUrlPromise = api('GET', '/tools')
      .then((data) => {
        const s = (data && Array.isArray(data.tools))
          ? data.tools.find((t) => t && t.key === 'scanner') : null;
        return (s && s.external_url) || _SCANNER_FALLBACK_URL;
      })
      .catch(() => _SCANNER_FALLBACK_URL);
  }
  return _scannerUrlPromise;
}

/* 側欄「台股掃描器」點擊 handler — 同 openQuantDataTab 模式：click 手勢內
 * 同步 window.open 過 popup blocker → 品牌過場 → getScannerUrl() 解析後導向
 * （首次要等 /api/tools，之後命中快取幾乎即時）。側欄 <a href> 保留硬編
 * fallback URL 作中鍵/Ctrl+click 後備。 */
function openScannerTab(toast) {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    if (toast) toast('瀏覽器擋下了新分頁，請允許本站的彈出視窗後重試', 'error');
    else console.error('openScannerTab: popup blocked');
    return;
  }
  _writeExtTabInterstitial(win, '台股掃描器');
  getScannerUrl().then((url) => { win.location.replace(url); });
}

/* ─────────────  最近使用（分析工作台）— localStorage 極小 helper  ─────────────
 * hub 頁（hr-analyst-hub.jsx）點工具卡 / 個股快查選股時 push；「最近使用」區
 * 渲染 list() 前 6 筆。entry 形狀：{ label, sub, hash }（hash 為 SPA 路由；
 * tab 型工具可另帶自訂欄位如 tab，helper 原樣存取不解讀）。
 * localStorage 不可用（隱私模式等）一律靜默降級。 */
const GSRecent = {
  _KEY: 'gs_recent_v1',
  _MAX: 8,
  push(entry) {
    if (!entry || !entry.label) return;
    try {
      const cur = this.list().filter(e => !(e.label === entry.label && e.hash === entry.hash));
      cur.unshift({ ...entry, ts: Date.now() });
      localStorage.setItem(this._KEY, JSON.stringify(cur.slice(0, this._MAX)));
    } catch (e) { /* 靜默 */ }
  },
  list() {
    try {
      const raw = JSON.parse(localStorage.getItem(this._KEY) || '[]');
      return Array.isArray(raw) ? raw.filter(e => e && e.label) : [];
    } catch (e) { return []; }
  },
};

/* 知識庫問答（gs-rag）的 openRagTab() 已於 2026-08-17 移除 —— 入口改為右下
 * 浮動聊天室（redesign/hr-rag-chat.jsx），直接以 Bearer JWT 打
 * GET /api/rag/api/ask，不需要新分頁也不需要 rag_session cookie。
 * 後端的 POST /api/rag/session 與 /api/rag/ 代理頁**保留可用**（中鍵開連結、
 * 除錯、日後要嵌回 iframe 都還在），只是前端不再主動走那條路。 */

/* ─────────────────────────  Right Rail (proposal 02 + 06 hybrid) ───
 * 280px sticky column for wide screens (≥1920). Hidden < 1480 via responsive.
 * Widgets (top → bottom):
 *   1. Market ticker (live from /api/market/quotes)
 *   2. World clocks (NY / London / HK) — passive ambient (proposal 06)
 *   3. Today's events (mock — will wire to /api/calendar/events when ready)
 *   4. Pending approvals (mock — uses FIX_LEAVE_REQUESTS / FIX_CLAIMS if loaded)
 *
 * Each widget is small (~120px) and quiet. The point is "ambient awareness"
 * — user shouldn't need to scan it actively.
 */

function MarketWidget() {
  const [quotes, setQuotes] = React.useState(null);
  React.useEffect(() => {
    const url = '/api/market/stream?symbols=' + encodeURIComponent('^TWII,WTX&,USD')
      + '&token=' + encodeURIComponent(getToken() || '');
    const es = new EventSource(url);
    es.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        setQuotes(data.quotes || []);
      } catch {}
    };
    return () => es.close();
  }, []);
  const SPECS = [
    { sym: '^TWII', label: '加權' },
    { sym: 'WTX&',  label: '台指近' },
    { sym: 'USD',   label: '美元' },
  ];
  return (
    <div style={{
      padding: '14px 16px', background: HR.ink, color: '#fff',
      borderRadius: 6, border: 'none',
    }}>
      <div style={{
        fontSize: FontSizes.trading_xs, color: 'rgba(255,255,255,0.5)',
        letterSpacing: '0.14em', textTransform: 'uppercase',
        marginBottom: 10, display: 'flex', justifyContent: 'space-between',
      }}>
        <span>Market · Live</span>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#00ff88' }} />
      </div>
      {SPECS.map(s => {
        const q = quotes && quotes.find(x => x.symbol === s.sym);
        const up = q && q.changePercent >= 0;
        return (
          <div key={s.sym} style={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            padding: '5px 0', fontFamily: HR.mono, fontSize: FontSizes.trading_xl,
          }}>
            <span style={{ color: 'rgba(255,255,255,0.6)' }}>{s.label}</span>
            <span style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <span style={{ color: '#fff', fontWeight: 500 }}>
                {q && q.price != null
                  ? (q.price >= 1000 ? q.price.toLocaleString() : q.price.toFixed(2))
                  : '—'}
              </span>
              {q && (
                <span style={{ fontSize: FontSizes.trading_sm, color: up ? '#52ff9a' : '#ff8080' }}>
                  {up ? '+' : ''}{(q.changePercent ?? 0).toFixed(2)}%
                </span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function QuickPunchWidget() {
  const [now, setNow] = React.useState(new Date());
  const [myEmp, setMyEmp] = React.useState(null);
  const [todayPunches, setTodayPunches] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [confirmOut, setConfirmOut] = React.useState(false);   // clock-out confirm (SPEC §5.1)
  const toast = typeof useToast !== 'undefined' ? useToast() : null;

  // Time ticking
  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Fetch employee details and today's punches
  const fetchData = async () => {
    try {
      let emp = myEmp;
      if (!emp) {
        emp = await api('GET', '/employees/me').catch(() => null);
        setMyEmp(emp);
      }
      if (emp && emp.id) {
        const today = new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Taipei' });
        const res = await api('GET', `/attendance/punches?employee_id=${emp.id}&from_date=${today}&to_date=${today}&page_size=20`).catch(() => ({ items: [] }));
        setTodayPunches(res.items || []);
      }
    } catch (e) {
      console.error('QuickPunch fetch failed:', e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData();
    const handlePunchUpdate = () => {
      fetchData();
    };
    window.addEventListener('roster:updated', handlePunchUpdate);
    return () => {
      window.removeEventListener('roster:updated', handlePunchUpdate);
    };
  }, [myEmp?.id]);

  const handlePunchIn = async () => {
    if (!myEmp || !myEmp.id || busy) return;
    setBusy(true);
    try {
      await api('POST', '/attendance/punches', { employee_id: myEmp.id, remark: null });
      if (toast) toast('已上班打卡，辛苦了！', 'ok');
      await fetchData();
      window.dispatchEvent(new CustomEvent('roster:updated'));
    } catch (e) {
      if (toast) toast(e.detail || '打卡失敗', 'error');
      else alert(e.detail || '打卡失敗');
    } finally {
      setBusy(false);
    }
  };

  // Runs only from the PunchOutConfirm dialog — the button just opens it (clock-in stays one tap).
  const handlePunchOut = async () => {
    const openPunch = todayPunches.find(p => !p.abort_time);
    if (!openPunch) { setConfirmOut(false); return; }
    if (busy) return;
    setBusy(true);
    try {
      await api('POST', `/attendance/punches/${openPunch.id}/close`);
      if (toast) toast('下班打卡成功，辛苦了！', 'ok');
      await fetchData();
      window.dispatchEvent(new CustomEvent('roster:updated'));
    } catch (e) {
      if (toast) toast(e.detail || '打卡失敗', 'error');
      else alert(e.detail || '打卡失敗');
    } finally {
      setBusy(false);
      setConfirmOut(false);
    }
  };

  const openPunch = todayPunches.find(p => !p.abort_time);
  const isCheckedIn = !!openPunch;
  const askPunchOut = () => { if (isCheckedIn && !busy) setConfirmOut(true); };

  const p = (n) => String(n).padStart(2, '0');
  const clockStr = `${p(now.getHours())}:${p(now.getMinutes())}:${p(now.getSeconds())}`;
  const dateStr = now.toLocaleDateString('zh-TW', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });

  let elapsedStr = '';
  if (isCheckedIn && openPunch.punch_time) {
    const elapsedMs = Math.max(0, now - new Date(openPunch.punch_time));
    const totalSec = Math.floor(elapsedMs / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    elapsedStr = `${p(h)}:${p(m)}:${p(s)}`;
  }

  const completedPunches = todayPunches.filter(p => !!p.abort_time);
  const totalCompletedMins = completedPunches.reduce((sum, p) => sum + (p.work_minutes || 0), 0);
  const compH = Math.floor(totalCompletedMins / 60);
  const compM = totalCompletedMins % 60;
  const totalCompletedStr = totalCompletedMins > 0 ? `今日已完成工時：${compH} 小時 ${compM} 分鐘` : '';

  if (loading) {
    return (
      <div style={{
        padding: '14px 16px', background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6,
      }}>
        <div style={{ fontSize: FontSizes.trading_xs, color: HR.inkFaint, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>
          快速打卡
        </div>
        <div style={{ fontSize: FontSizes.trading_md, color: HR.inkFaint, fontStyle: 'italic' }}>載入中…</div>
      </div>
    );
  }

  if (!myEmp) {
    return (
      <div style={{
        padding: '14px 16px', background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6,
      }}>
        <div style={{ fontSize: FontSizes.trading_xs, color: HR.inkFaint, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>
          快速打卡
        </div>
        <div style={{ fontSize: FontSizes.trading_md, color: HR.down }}>
          尚未綁定員工帳號
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '14px 16px', background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{
        fontSize: FontSizes.trading_xs, color: HR.inkFaint,
        letterSpacing: '0.14em', textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <span>快速打卡</span>
        <span style={{ fontSize: FontSizes.trading_xxs, color: isCheckedIn ? HR.up : HR.inkFaint, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: isCheckedIn ? HR.up : HR.inkFaint,
            boxShadow: isCheckedIn ? `0 0 4px ${GxCore.alpha(HR.up, '88')}` : 'none',
          }} />
          {isCheckedIn ? '上班中' : '未打卡'}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', padding: '6px 0', borderBottom: `1px dashed ${HR.ruleSoft}` }}>
        <div style={{ fontFamily: HR.mono, fontSize: FontSizes.lg, fontWeight: 600, color: HR.ink, fontVariantNumeric: 'tabular-nums' }}>
          {clockStr}
        </div>
        <div style={{ fontSize: FontSizes.trading_xxs, color: HR.inkFaint }}>
          {dateStr}
        </div>
      </div>

      <div style={{ fontSize: FontSizes.trading_xs, color: HR.inkSoft, textAlign: 'center', minHeight: 16 }}>
        {isCheckedIn ? (
          <div>
            已上班：<strong style={{ fontFamily: HR.mono, color: HR.accent, fontVariantNumeric: 'tabular-nums' }}>{elapsedStr}</strong>
          </div>
        ) : (
          <div>
            {totalCompletedStr || '今日尚未開始打卡'}
          </div>
        )}
      </div>

      <button
        type="button"
        disabled={busy}
        onClick={isCheckedIn ? askPunchOut : handlePunchIn}
        style={{
          width: '100%',
          padding: '10px 14px',
          borderRadius: 6,
          background: isCheckedIn ? HR.down : HR.accent,
          color: '#FFFFFF',
          border: 'none',
          cursor: busy ? 'not-allowed' : 'pointer',
          fontWeight: 600,
          fontSize: FontSizes.trading_md,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          boxShadow: isCheckedIn ? `0 2px 6px ${GxCore.alpha(HR.down, '22')}` : `0 2px 6px ${GxCore.alpha(HR.accent, '22')}`,
          transition: 'all 150ms ease-in-out',
        }}
        onMouseEnter={(e) => {
          if (!busy) {
            e.currentTarget.style.opacity = '0.9';
            e.currentTarget.style.transform = 'translateY(-0.5px)';
          }
        }}
        onMouseLeave={(e) => {
          if (!busy) {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.transform = 'translateY(0)';
          }
        }}
      >
        <Icon name="clock" size={12} stroke={2.5} />
        {isCheckedIn ? '下班打卡' : '上班打卡'}
      </button>
      <PunchOutConfirm open={confirmOut && isCheckedIn} busy={busy}
        onCancel={() => setConfirmOut(false)} onConfirm={handlePunchOut} />
    </div>
  );
}

function TodayEventsWidget() {
  // Pull from mock fixtures if loaded; otherwise empty.
  const events = React.useMemo(() => {
    const t = todayStr();
    // FIX_LEAVE_REQUESTS shape from hr-leave.jsx, optional (mock fixture，未接 API)
    // 比較邏輯與 variant-a-v2 TodayMini 對齊：有完整 timestamp 就比時間（進行中才算），
    // 否則退回純日期欄位（from_date/to_date）比對
    const nowTs = new Date();
    const leave = (window.FIX_LEAVE_REQUESTS || [])
      .filter(r => r.status === 'pending' && (
        (r.start_at && r.end_at)
          ? (new Date(r.start_at) <= nowTs && nowTs < new Date(r.end_at))
          : (r.from_date <= t && r.to_date >= t)
      ))
      .slice(0, 2)
      .map(r => ({ kind: 'leave', label: `${r.employee_name} 請假中`, color: HR.warn }));
    // Fake fixed events as filler
    const filler = [
      { kind: 'meeting', label: '14:00 月度業績檢討', color: HR.accent, time: '14:00' },
    ];
    return [...leave, ...filler].slice(0, 4);
  }, []);
  return (
    <div style={{
      padding: '14px 16px', background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6,
      display: 'flex', flexDirection: 'column', flex: '1 1 0%', minHeight: 120,
    }}>
      <div style={{
        fontSize: FontSizes.trading_xs, color: HR.inkFaint,
        letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10,
        display: 'flex', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <span>Today · 今日</span>
        <a href="#/calendar" style={{ color: HR.accent, textDecoration: 'none' }}>→</a>
      </div>
      <div className="v2-scroll" style={{ flex: 1, overflowY: 'auto', margin: '0 -4px' }}>
        {events.length === 0 ? (
          <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontStyle: 'italic', padding: '0 4px' }}>今日無事件</div>
        ) : events.map((ev, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'flex-start', gap: 7, padding: '4px 4px',
            fontSize: FontSizes.trading_xl, color: HR.ink,
          }}>
            <span style={{ width: 5, height: 5, borderRadius: 1, background: ev.color, marginTop: 6, flexShrink: 0 }} />
            <span>{ev.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PendingApprovalsWidget({ user }) {
  // Show counts of mock pending leave / expense if available
  const leaveCount = ((window.FIX_LEAVE_REQUESTS) || []).filter(r => r.status === 'pending').length;
  const claimCount = ((window.FIX_CLAIMS) || []).filter(c => c.status === 'pending').length;
  const total = leaveCount + claimCount;
  const isMgr = hasRole(user, 'manager', 'finance') || isAdmin(user);
  if (!isMgr || total === 0) return null;
  return (
    <div style={{
      padding: '14px 16px', background: '#FAEFD8', border: `1px solid ${GxCore.alpha(HR.warn, '33')}`,
      borderRadius: 6, borderLeft: `3px solid ${HR.warn}`,
      display: 'flex', flexDirection: 'column', flex: '1 1 0%', minHeight: 120,
    }}>
      <div style={{
        fontSize: FontSizes.trading_xs, color: HR.warn,
        letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8, fontWeight: 600,
        flexShrink: 0,
      }}>待我審核 · {total}</div>
      <div className="v2-scroll" style={{ flex: 1, overflowY: 'auto', margin: '0 -4px' }}>
        {leaveCount > 0 && (
          <a href="#/leave" style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '6px 4px', fontSize: FontSizes.xxs, color: HR.ink, textDecoration: 'none',
          }}>
            <span>請假申請</span>
            <span style={{ fontFamily: HR.mono, fontWeight: 600 }}>{leaveCount}</span>
          </a>
        )}
        {claimCount > 0 && (
          <a href="#/expense" style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '6px 4px', fontSize: FontSizes.xxs, color: HR.ink, textDecoration: 'none',
          }}>
            <span>報帳單</span>
            <span style={{ fontFamily: HR.mono, fontWeight: 600 }}>{claimCount}</span>
          </a>
        )}
      </div>
    </div>
  );
}

function RightRail({ user, onLogout }) {
  const currentUser = user || window._USER;

  return (
    <aside className="hr-right-rail" style={{
      padding: '20px 16px 24px', display: 'flex', flexDirection: 'column', gap: 12,
      position: 'sticky', top: 0, alignSelf: 'start', height: '100vh',
      overflow: 'hidden', borderLeft: `1px solid ${HR.rule}`,
      background: HR.bg,
    }}>

      <MarketWidget />
      <QuickPunchWidget />
      <TodayEventsWidget />
      <PendingApprovalsWidget user={currentUser} />

      <div style={{
        marginTop: 'auto', paddingTop: 16, borderTop: `1px solid ${HR.rule}`,
        fontSize: FontSizes.trading_sm, color: HR.inkFaint, fontFamily: HR.mono, letterSpacing: '0.06em',
        textAlign: 'center', flexShrink: 0,
      }}>
        Genesis · ambient panel
      </div>


    </aside>
  );
}

function Sidebar({ activeRoute, currentUser, onLogout }) {
  const [changePwOpen, setChangePwOpen] = React.useState(false);
  const handleLogout = () => {
    if (typeof onLogout === 'function') {
      onLogout();
    } else {
      localStorage.removeItem('token');
      location.replace('/static/login.html');
    }
  };
  // Filter routes by current user's role permissions
  const visibleRoutes = ROUTES.filter(r => canAccessRoute(currentUser, r.key));
  const grouped = visibleRoutes.reduce((acc, r) => {
    (acc[r.section] = acc[r.section] || []).push(r);
    return acc;
  }, {});

  return (
    <aside style={{
      background: '#F4F1E9', borderRight: `1px solid ${HR.rule}`,
      padding: '28px 12px 20px', display: 'flex', flexDirection: 'column',
      position: 'sticky', top: 0, alignSelf: 'start', height: '100vh',
    }}>
      <div style={{ padding: '0 12px 24px', borderBottom: `1px solid ${HR.rule}`, marginBottom: 20 }}>
        <a
          href="/static/index.html"
          title="返回交易儀表板"
          style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: 'inherit' }}
        >
          <div style={{
            width: 34, height: 34, borderRadius: 6, background: HR.accent, color: '#fff',
            display: 'grid', placeItems: 'center',
            fontFamily: HR.serif, fontWeight: 600, fontSize: FontSizes.base, letterSpacing: '-0.02em',
          }}>G</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: HR.serif, fontSize: FontSizes.base, fontWeight: 500, letterSpacing: '-0.01em', lineHeight: 1.1, color: HR.ink }}>
              Genesis
            </div>
            <div style={{ fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 2 }}>
              Workspace
            </div>
          </div>
        </a>
      </div>

      <nav style={{ flex: 1, padding: '0 12px', overflowY: 'auto' }}>
        {Object.entries(grouped).map(([section, items]) => (
          <div key={section} style={{ marginBottom: 22 }}>
            <div style={{
              fontSize: FontSizes.trading_sm, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: HR.inkFaint, padding: '0 12px 8px',
            }}>{section}</div>
            {items.map(it => {
              const active = it.key === activeRoute;
              return (
                <a
                  key={it.key}
                  href={'#/' + it.key}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.5)'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 12px', borderRadius: 8, fontSize: FontSizes.trading_md,
                    color: active ? '#fff' : HR.inkSoft,
                    background: active ? HR.accent : 'transparent',
                    fontWeight: active ? 600 : 400,
                    textDecoration: 'none', position: 'relative',
                    boxShadow: active ? '0 4px 12px rgba(11, 95, 90, 0.2)' : 'none',
                    marginBottom: 2,
                    transition: 'all 120ms',
                  }}
                >
                  <Icon name={it.icon} size={15} />
                  <span style={{ flex: 1 }}>{it.label}</span>
                  {it.mock && (
                    <span style={{
                      fontSize: FontSizes.trading_xxs, padding: '1px 5px', borderRadius: 3,
                      background: HR.warn, color: '#fff', fontWeight: 600,
                      letterSpacing: '0.05em', fontFamily: HR.mono,
                    }}>MOCK</span>
                  )}
                </a>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User Identity Widget at the bottom of sidebar */}
      {currentUser && (
        <div style={{ 
          padding: '12px 12px', 
          borderTop: `1px solid ${HR.rule}`, 
          marginTop: 'auto', // push to bottom below nav
          flexShrink: 0 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 30, height: 30, borderRadius: '50%', background: '#D6CEB9', color: HR.ink,
              display: 'grid', placeItems: 'center', fontSize: FontSizes.xxs, fontWeight: 600,
            }}>
              {(currentUser.employee_name || currentUser.display_name || currentUser.username || '?').slice(0, 1).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: FontSizes.trading_md, fontWeight: 500, color: HR.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {currentUser.employee_name || currentUser.display_name || currentUser.username || '—'}
              </div>
              <div style={{ fontSize: FontSizes.trading_md, color: HR.inkFaint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {currentUser.username} · {(currentUser.roles || []).join(', ') || currentUser.role || '—'}
              </div>
            </div>
            <button
              onClick={() => setChangePwOpen(true)}
              title="修改密碼"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: HR.inkSoft, padding: 4 }}
            >
              <Icon name="key" size={14} />
            </button>
            <button
              onClick={handleLogout}
              title="登出"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: HR.inkSoft, padding: 4 }}
            >
              <Icon name="log-out" size={14} />
            </button>
          </div>
        </div>
      )}


      {changePwOpen && <ChangePasswordModal onClose={() => setChangePwOpen(false)} />}
    </aside>
  );
}

function ChangePasswordModal({ onClose }) {
  const toast = useToast();
  const [oldPw, setOldPw] = React.useState('');
  const [newPw, setNewPw] = React.useState('');
  const [confirmPw, setConfirmPw] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    if (!oldPw || !newPw || !confirmPw) { toast('請填寫所有欄位', 'error'); return; }
    if (newPw.length < 8) { toast('新密碼至少 8 個字元', 'error'); return; }
    if (newPw !== confirmPw) { toast('兩次新密碼不一致', 'error'); return; }
    if (oldPw === newPw) { toast('新密碼不能與舊密碼相同', 'error'); return; }
    setBusy(true);
    try {
      await api('POST', '/auth/change-password', { old_password: oldPw, new_password: newPw });
      toast('密碼已更新', 'ok');
      onClose();
    } catch (e) {
      toast(e.detail || '修改失敗', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title="修改密碼"
      subtitle="Change Password"
      size="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>取消</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>{busy ? '更新中…' : '確認更新'}</Button>
      </>}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="舊密碼" required>
          <input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)}
            style={inputStyle()} autoComplete="current-password" autoFocus />
        </Field>
        <Field label="新密碼" hint="至少 8 個字元">
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
            style={inputStyle()} autoComplete="new-password" />
        </Field>
        <Field label="確認新密碼" required>
          <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
            style={inputStyle()} autoComplete="new-password" />
        </Field>
      </div>
    </Modal>
  );
}

function PageHeader({ eyebrow, title, italic, action }) {
  return (
    <div className="va2-page-header" style={uiStyle({
      display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
      paddingBottom: 22, borderBottom: `2px solid ${HR.ink}`, marginBottom: 28,
    }, { borderBottom: 'none', paddingBottom: 0, marginBottom: 24, gap: 16, flexWrap: 'wrap' })}>
      <div>
        <div style={uiStyle({ fontSize: FontSizes.trading_lg, letterSpacing: '0.24em', textTransform: 'uppercase', color: HR.inkFaint, marginBottom: 8 }, {
          fontSize: 13, fontWeight: 700, color: GxT.color.p, letterSpacing: 0, textTransform: 'none', marginBottom: 4,
        })}>
          {eyebrow}
        </div>
        <h1 style={uiStyle({
          fontFamily: HR.serif, fontSize: FontSizes.xl, fontWeight: 400,
          letterSpacing: '-0.02em', lineHeight: 1, margin: 0, color: HR.ink,
        }, { fontFamily: GxT.font, fontSize: GxT.size.h1, fontWeight: GxT.weight.display, letterSpacing: '-0.5px', lineHeight: 1.12 })}>
          {title}{italic && <span className="va2-page-title-italic" style={uiStyle({ color: HR.inkFaint, fontStyle: 'italic' }, {
            fontStyle: 'normal', fontWeight: 600, fontSize: '0.5em', letterSpacing: 0, color: GxT.color.faint,
          })}> — {italic}</span>}
        </h1>
      </div>
      {action}
    </div>
  );
}

function Card({ eyebrow, title, italic, right, children, padding, className }) {
  // `=== undefined` (not `??`): the old default parameter only applied to undefined, so a caller's
  // explicit null must still reach the style untouched (plan review #9).
  const bodyPadding = padding === undefined ? uiPick('22px 24px', '14px 20px 20px') : padding;
  return (
    <div className={className} style={uiStyle({ background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6, overflow: 'hidden' }, {
      background: GxT.color.card, border: 'none', borderRadius: GxT.radius.card,
    })}>
      {(eyebrow || title || right) && (
        <div style={uiStyle({
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: `1px solid ${HR.rule}`, gap: 16,
        }, { padding: '18px 20px 0', borderBottom: 'none' })}>
          <div>
            {eyebrow && <div style={uiStyle({ fontSize: FontSizes.trading_lg, letterSpacing: '0.14em', textTransform: 'uppercase', color: HR.inkFaint, marginBottom: 4 }, {
              fontSize: 12, fontWeight: 700, color: GxT.color.p, letterSpacing: 0, textTransform: 'none',
            })}>{eyebrow}</div>}
            {title && (
              <h3 style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.lg, fontWeight: 400, margin: 0, letterSpacing: '-0.015em' }, {
                fontFamily: GxT.font, fontSize: GxT.size.h3, fontWeight: GxT.weight.title, letterSpacing: 0, lineHeight: 1.3, color: GxT.color.on,
              })}>
                {title}{italic && <span style={uiStyle({ color: HR.inkFaint, fontStyle: 'italic' }, {
                  // absolute 13px (not 0.5em): half of a 17px title would be unreadable
                  fontStyle: 'normal', fontWeight: 600, fontSize: GxT.size.label, color: GxT.color.faint,
                })}> — {italic}</span>}
              </h3>
            )}
          </div>
          {right}
        </div>
      )}
      <div style={{ padding: bodyPadding }}>{children}</div>
    </div>
  );
}

// 把 Markdown 渲染成排版內容：marked 轉 HTML → DOMPurify 消毒 → 注入。
// marked / DOMPurify 由 index.html 以全域 <script>（vendored，免 CDN）載入。
// 防呆：lib 未載入或解析失敗 → 退回純文字 pre-wrap，畫面不崩。
// 用 className="ar-markdown"（樣式定義在 index.html 全域 <style>）。
function MarkdownView({ markdown, className }) {
  const md = markdown || '';
  const html = React.useMemo(() => {
    if (!md) return '';
    const marked = typeof window !== 'undefined' && window.marked;
    const purify = typeof window !== 'undefined' && window.DOMPurify;
    if (!marked || !purify) return null;  // lib 沒載到 → 走純文字保底
    try {
      const raw = marked.parse(String(md), { gfm: true, breaks: false });
      return purify.sanitize(raw);
    } catch (e) {
      return null;
    }
  }, [md]);

  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!ref.current) return;
    // 外部連結一律開新分頁（內容來自外部來源，安全考量）
    ref.current.querySelectorAll('a[href]').forEach(a => {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    });
  }, [html]);

  if (html === null) {
    return <div className={className} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{md}</div>;
  }
  return <div ref={ref} className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

function EmptyState({ icon = 'folder', title, hint, action }) {
  return (
    <div style={{ padding: '60px 20px', textAlign: 'center', color: HR.inkFaint }}>
      <div style={uiStyle({ display: 'inline-flex', padding: 14, borderRadius: '50%', background: HR.surfaceAlt, marginBottom: 14, color: HR.inkSoft }, {
        borderRadius: GxT.radius.card, background: GxT.color.pc, color: GxT.color.p,
      })}>
        <Icon name={icon} size={20} />
      </div>
      <div style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.lg, color: HR.ink, marginBottom: 6 }, {
        fontFamily: GxT.font, fontSize: GxT.size.h3, fontWeight: GxT.weight.title,
      })}>{title}</div>
      {hint && <div style={uiStyle({ fontSize: FontSizes.xs, marginBottom: 16 }, { fontSize: 14, color: GxT.color.onV })}>{hint}</div>}
      {action}
    </div>
  );
}

function Spinner({ size = 16 }) {
  // SVG presentation attributes get concrete colours (GxCore.hex; Bento literal passes through)
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ animation: 'hr-spin 0.8s linear infinite' }}>
      <circle cx="12" cy="12" r="9" fill="none" stroke={GxCore.hex(HR.rule)} strokeWidth="2.5" />
      <path d="M12 3 a9 9 0 0 1 9 9" fill="none" stroke={GxCore.hex(HR.accent)} strokeWidth="2.5" strokeLinecap="round" />
      <style>{`@keyframes hr-spin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  );
}

/* ─────────────────────────  Skeleton primitives  ─────────────────
 * Replace full-page <Spinner> with content-shape placeholders.
 * Visual: light-grey block with subtle shimmer sweep.
 *
 * Use:
 *   <Skeleton width="60%" height={14} />            // generic block
 *   <SkeletonText lines={3} />                       // 3 lines of varying width
 *   <SkeletonAvatar size={28} />                     // circle
 *   <SkeletonRow cols={[80, '1fr', '1fr', 80]} />    // table row mimic
 *   <SkeletonTable rows={6} cols={[80, '1fr', '1fr', 80]} />
 */
function Skeleton({ width = '100%', height = 12, borderRadius, style }) {
  return (
    <div className="hr-skeleton" style={{
      width, height, borderRadius: borderRadius === undefined ? uiPick(3, 8) : borderRadius,
      background: uiPick(HR.ruleSoft, GxT.color.s3), position: 'relative', overflow: 'hidden',
      ...(style || {}),
    }}>
      <style>{`
        @keyframes hr-skeleton-shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .hr-skeleton::after {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%);
          animation: hr-skeleton-shimmer 1.4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

function SkeletonText({ lines = 3, gap = 8 }) {
  const widths = ['100%', '92%', '78%', '85%', '70%'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} width={widths[i % widths.length]} height={11} />
      ))}
    </div>
  );
}

function SkeletonAvatar({ size = 28 }) {
  return <Skeleton width={size} height={size} borderRadius="50%" />;
}

function SkeletonRow({ cols = [80, '1fr', '1fr', 80], avatarFirst = false }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: cols.join(' '), gap: 14,
      alignItems: 'center', padding: '14px 24px',
      borderTop: `1px solid ${HR.ruleSoft}`,
    }}>
      {cols.map((c, i) => (
        i === 0 && avatarFirst
          ? <SkeletonAvatar key={i} size={24} />
          : <Skeleton key={i} width={i === 1 ? '70%' : '60%'} height={12} />
      ))}
    </div>
  );
}

function SkeletonTable({ rows = 6, cols, avatarFirst = false, header = true }) {
  const c = cols || [120, '1.4fr', '1fr', '1fr', 100, 80];
  return (
    <div>
      {header && (
        <div style={{
          display: 'grid', gridTemplateColumns: c.join(' '), gap: 14,
          padding: '11px 24px', background: HR.surfaceAlt,
          borderBottom: `1px solid ${HR.rule}`,
        }}>
          {c.map((_, i) => <Skeleton key={i} width="50%" height={10} />)}
        </div>
      )}
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonRow key={i} cols={c} avatarFirst={avatarFirst} />
      ))}
    </div>
  );
}

function SkeletonCard({ height = 120 }) {
  return (
    <div style={uiStyle({
      padding: '20px 24px', background: HR.surface,
      border: `1px solid ${HR.rule}`, borderRadius: 6,
      display: 'flex', flexDirection: 'column', gap: 12, height,
    }, { padding: '18px 20px', background: GxT.color.card, border: 'none', borderRadius: GxT.radius.card })}>
      <Skeleton width="40%" height={10} />
      <Skeleton width="65%" height={26} />
      <Skeleton width="50%" height={10} />
    </div>
  );
}

/* ─────────────────────────  Pagination  ────────────────────────
 * Generic pagination control. Originally in hr-employees.jsx; lifted to shell
 * so Audit/Invoices/Payslips/Punches etc. can reuse without copy-paste.
 *
 * Props:
 *   page          (1-based current page)
 *   pageSize      (rows per page)
 *   total         (total record count)
 *   onPageChange(newPage)
 *   onPageSizeChange(newSize)  — optional; omit to hide pageSize selector
 */
function Pagination({ page, pageSize, total, onPageChange, onPageSizeChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const _inputStyle = uiStyle({
    padding: '4px 8px', fontSize: FontSizes.xxs, fontFamily: HR.sans,
    border: `1px solid ${HR.rule}`, borderRadius: 4,
    background: HR.surface, color: HR.ink, outline: 'none',
  }, {
    // Expressive: compact outlined field (same language as inputStyle)
    padding: '4px 10px', minHeight: 32, fontSize: 13, fontFamily: GxT.font,
    border: `1px solid ${GxT.color.ol}`, borderRadius: GxT.radius.btnSm,
    background: GxT.color.card, color: GxT.color.on,
  });
  const gxNum = { fontFamily: GxT.font, fontVariantNumeric: 'tabular-nums' };

  return (
    <div className="hr-pagination" style={uiStyle({
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 24px', borderTop: `1px solid ${HR.rule}`, fontSize: FontSizes.xxs, color: HR.inkSoft,
    }, { padding: '12px 20px', borderTop: `1px solid ${GxT.color.s3}`, fontSize: 13, gap: 12, flexWrap: 'wrap' })}>
      <div style={uiStyle({ fontFamily: HR.mono }, gxNum)}>
        {total === 0 ? '無資料' : `${from}–${to} / ${total}`}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {onPageSizeChange && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={uiStyle({ fontSize: FontSizes.trading_lg, letterSpacing: '0.08em', textTransform: 'uppercase', color: HR.inkFaint }, {
              fontSize: 13, fontWeight: 700, letterSpacing: 0, textTransform: 'none', color: GxT.color.onV,
            })}>每頁</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
              style={_inputStyle}
            >
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>← 上一頁</Button>
          <span style={uiStyle({ padding: '0 10px', fontFamily: HR.mono }, gxNum)}>
            第 {page} / {totalPages} 頁
          </span>
          <Button size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>下一頁 →</Button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────  Confirm dialog (helper)  ─────────── */

function ConfirmDialog({
  open, title, message, danger, onCancel, onConfirm, busy,
  confirmLabel,  // 可選；不傳則用預設「確認刪除」(danger) / 「確認」
}) {
  const defaultLabel = danger ? '確認刪除' : '確認';
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      subtitle="確認"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>取消</Button>
          <Button
            variant={danger ? 'primary' : 'primary'}
            onClick={onConfirm}
            disabled={busy}
            style={danger ? uiStyle({ background: HR.down, boxShadow: '0 1px 0 rgba(155,51,48,0.3)' }, { background: GxT.color.down, boxShadow: 'none' }) : undefined}
          >
            {busy ? '處理中…' : (confirmLabel || defaultLabel)}
          </Button>
        </>
      }
    >
      <div style={uiStyle({
        fontSize: FontSizes.trading_md, color: HR.inkSoft, lineHeight: 1.6,
        whiteSpace: 'pre-line',  // 讓 message 內的 \n 換行顯示
      }, { fontSize: 14, color: GxT.color.onV })}>{message}</div>
    </Modal>
  );
}

/* ─────────────────────────  Clock-out confirm  ─────────────────
 * Owner decision (2026-10-19, SPEC §5.1): every 下班打卡 entry point — Bento QuickPunchWidget /
 * QuickPunchMini, the #/attendance kiosk, and the Expressive GxPunchProvider (top-bar pill, mobile
 * FAB, home card) — shows this in-page confirm before POST /attendance/punches/{id}/close.
 * 上班打卡 stays one tap. Closed → renders nothing (Modal returns null), so callers can always
 * mount it. The time line ticks while open and is always Asia/Taipei HH:mm (fmtTime). */
function punchOutConfirmMessage(nowMs) {
  return `現在時間 ${fmtTime(new Date(nowMs))}（台北時間）\n送出後即記錄下班時間；如需更正請洽 HR。`;
}

function PunchOutConfirm({ open, busy, onCancel, onConfirm }) {
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!open) return undefined;
    setNowMs(Date.now());
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open]);
  if (!open) return null;
  return (
    <ConfirmDialog
      open
      title="確定下班打卡？"
      message={punchOutConfirmMessage(nowMs)}
      confirmLabel="下班打卡"
      busy={busy}
      // Overlay click / 取消 are ignored while the request is in flight.
      onCancel={() => { if (!busy && onCancel) onCancel(); }}
      onConfirm={onConfirm}
    />
  );
}

/* ─────────────────────────  Date helpers  ───────────────────── */

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  // 強制公司時區 Asia/Taipei（對齊 fmtTime）：打卡等事件以台北業務日為準。
  // 不可用 toISOString()（UTC）取日期，否則台北 00:00–08:00 的時間戳會顯示成前一天。
  return d.toLocaleDateString('sv-SE', { timeZone: 'Asia/Taipei' });
}

function fmtDateTime(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function fmtTime(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  // 強制公司時區 Asia/Taipei — 出勤打卡是「事件發生地時間」，與觀看者所在地無關
  return d.toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', hour12: false,
    timeZone: 'Asia/Taipei',
  });
}

/** minutes -> "8h 32m" */
function fmtDuration(mins) {
  if (mins == null || isNaN(mins)) return '—';
  const sign = mins < 0 ? '−' : '';
  const m = Math.abs(mins);
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${sign}${h}h ${r}m`;
  if (h) return `${sign}${h}h`;
  return `${sign}${r}m`;
}

function fmtMoney(n, currency = 'NT$') {
  if (n == null || isNaN(n)) return '—';
  return `${currency}${Math.round(n).toLocaleString()}`;
}

// 用 TPE 業務日（避免 UTC 切片在台北凌晨 0~8 點回到前一天）。
// 'sv-SE' locale 輸出 'YYYY-MM-DD' 格式。
function todayStr() {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Taipei' });
}

function nDaysAgoStr(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

/* ─────────────────────────  Tabs  ───────────────────────────── */

function Tabs({ tabs, value, onChange }) {
  return (
    <div className="hr-tabs-bar" style={uiStyle({
      display: 'flex', gap: 0, borderBottom: `1px solid ${HR.rule}`,
      marginBottom: 24,
    }, { gap: 8, borderBottom: 'none', marginBottom: 20, overflowX: 'auto', scrollbarWidth: 'none' })}>
      {tabs.map(t => {
        const active = t.key === value;
        // Expressive: M3 chip-style tabs (tonal active, transparent inactive)
        return (
          <button
            key={t.key}
            className="hr-tab-btn"
            onClick={() => onChange(t.key)}
            style={uiStyle({
              padding: '12px 18px', fontSize: FontSizes.xs,
              background: 'transparent', border: 'none',
              borderBottom: `2px solid ${active ? HR.accent : 'transparent'}`,
              color: active ? HR.ink : HR.inkSoft,
              fontWeight: active ? 500 : 400, cursor: 'pointer',
              fontFamily: HR.sans, marginBottom: -1,
              display: 'inline-flex', alignItems: 'center', gap: 7,
            }, {
              minHeight: 40, padding: '0 16px', fontSize: 14, borderRadius: GxT.radius.btn,
              borderBottom: 'none', marginBottom: 0, fontWeight: GxT.weight.label, fontFamily: GxT.font,
              background: active ? GxT.color.sc : 'transparent', color: active ? GxT.color.onSc : GxT.color.onV,
              flexShrink: 0, whiteSpace: 'nowrap',
              transition: 'background .2s, border-radius .4s var(--gx-spring)',
            })}
          >
            {t.icon && <Icon name={t.icon} size={uiPick(13, 16)} />}
            {t.label}
            {t.count != null && (
              <span style={uiStyle({
                fontSize: FontSizes.trading_sm, padding: '1px 6px', borderRadius: 8,
                background: active ? HR.accentSoft : HR.ruleSoft,
                color: active ? HR.accent : HR.inkSoft,
                fontFamily: HR.mono, fontWeight: 500,
              }, {
                borderRadius: GxT.radius.chipSm, background: active ? GxT.color.card : GxT.color.s2,
                fontFamily: GxT.font, fontWeight: GxT.weight.label, fontVariantNumeric: 'tabular-nums',
              })}>{t.count}</span>
            )}
            {t.badge && (
              <span style={uiStyle({
                fontSize: FontSizes.trading_xxs, padding: '1px 5px', borderRadius: 3,
                background: HR.warn, color: '#fff', fontWeight: 600,
                letterSpacing: '0.05em', fontFamily: HR.mono,
              }, {
                borderRadius: GxT.radius.chipSm, background: GxT.tint.new.bg, color: GxT.tint.new.fg,
                fontWeight: GxT.weight.label, letterSpacing: 0, fontFamily: GxT.font,
              })}>{t.badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ─────────────────────────  MockBanner  ─────────────────────── */

function MockBanner({ children }) {
  return (
    <div style={uiStyle({
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 16px', marginBottom: 20,
      background: '#FAEFD8', border: `1px solid ${GxCore.alpha(HR.warn, '33')}`,
      borderLeft: `3px solid ${HR.warn}`, borderRadius: 4,
      fontSize: FontSizes.trading_md, color: HR.ink,
    }, {
      padding: '12px 16px', background: GxT.tint.warn.bg, border: 'none', borderLeft: 'none',
      borderRadius: GxT.radius.inner, fontSize: 14, color: GxT.tint.warn.fg,
    })}>
      <span style={uiStyle({
        fontSize: FontSizes.trading_xxs, padding: '2px 6px', borderRadius: 3,
        background: HR.warn, color: '#fff', fontWeight: 600,
        letterSpacing: '0.05em', fontFamily: HR.mono,
      }, {
        // #9A6A1E (not HR.warn #A56A1F) keeps white 11px text at AA
        borderRadius: GxT.radius.chipSm, background: GxT.tint.new.bg, color: GxT.tint.new.fg,
        fontWeight: GxT.weight.label, letterSpacing: 0, fontFamily: GxT.font,
      })}>MOCK</span>
      <span style={{ flex: 1 }}>{children}</span>
    </div>
  );
}

// Note: The "hr" role has been restricted to only have lunch admin privileges.
// For all other ERP functions (like Attendance), "hr" acts as a standard employee.
// Therefore, isHrOrAdmin only returns true for "admin" to prevent "hr" from seeing ERP admin views.
function isHrOrAdmin(user) {
  if (!user) return false;
  const roles = user.roles || [];
  return roles.includes('admin') || user.role === 'admin';
}

// isAttendanceManager — 專供「出勤系統」管理視圖判斷（打卡 / 月曆 / 每日彙整 / 排班管理外的彙整）。
// 比照 isHrOrAdmin，但額外認 "hr"：HR 在出勤系統享完整管理權限（後端 /api/attendance 已對 hr 放行）。
// 切勿用此 helper 控管員工 / 部門 / 薪資等其他模組——那些仍只給 admin（沿用 isHrOrAdmin）。
function isAttendanceManager(user) {
  if (!user) return false;
  const roles = user.roles || [];
  return roles.includes('admin') || roles.includes('hr')
    || user.role === 'admin' || user.role === 'hr';
}

/* ─────────────────────────  Public exports  ─────────────────── */

Object.assign(window, {
  HR, api, getToken, setToken,
  EnumProvider, EnumContext, useEnum, useServerEvent,
  useHashRoute, navigate, getHashQuery, setHashQuery,
  ToastProvider, useToast,
  Button, Pill, Field, TextInput, Select, Textarea, Checkbox,
  Modal, ConfirmDialog, PunchOutConfirm, punchOutConfirmMessage, Card, PageHeader, EmptyState, Spinner,
  Tabs, MockBanner, Sidebar, RightRail, LoginScreen, Forbidden,
  // Skeleton primitives (replaces full-page spinners with content-shape placeholders)
  Skeleton, SkeletonText, SkeletonAvatar, SkeletonRow, SkeletonTable, SkeletonCard,
  // Pagination (now generic — was duplicated in hr-employees.jsx)
  Pagination,
  fmtDate, fmtDateTime, fmtTime, fmtDuration, fmtMoney,
  todayStr, nDaysAgoStr, isHrOrAdmin, isAttendanceManager, inputStyle,
  ROUTES, EXTRA_ROUTES,
  // Role permission helpers
  userRoles, isAdmin, hasRole, isAnalystOnly, isNonStaffOnly, isRestrictedAccount,
  restrictedRouteKeys, canAccessRoute, canAccessToolLinks, hasCapability, hasAnyCapability,
  openQuantDataTab, openInsiderTab, openTxoBoardTab, openMarketCalendarTab, openFundRadarTab, openBigdayTab, openYtkbTab, openResearchEngineTab,
  openKellyGameTab, openExpertTrackerTab, openReleaseHubTab, openProgressLedgerTab, openLlmJudgeTab, openTeamRadarTab,
  openTraderReportTab,
  openTJCoachTab,
  getScannerUrl, openScannerTab,
  GSRecent,
});
